import { Component, OnInit } from '@angular/core';
import {
  ControlDpService,
  ControlDpEmployee,
  ControlDpOccurrence,
  ControlDpRole,
  ControlDpHistory,
  ControlDpAttachment,
  ControlDpImport,
  ControlDpFeedbackTemplate,
  ControlDpEmployeeFeedback,
  ControlDpFeedbackDashboard
} from '../../services/controldp.service';
import { ControlopService } from '../../services/controlop.service';
import { UserSessionService } from '../../services/user-session.service';
import { UserService } from '../../services/user.service';
import { OrgStructureService } from '../../services/org-structure.service';
import { ControlQuestionService, ControlQuestion, ControlQuestionOption } from '../../services/controlquestion.service';
import { MoodService } from '../../services/mood.service';
import { API_BASE_URL } from '../../services/api-base';
import { firstValueFrom } from 'rxjs';
import { jsPDF } from 'jspdf';
import * as XLSX from 'xlsx';
import { ChartConfiguration, ChartData } from 'chart.js';

type TabKey = 'dashboard' | 'collaborators' | 'imports' | 'extras' | 'business' | 'forecast' | 'closing' | 'reports';
type EmployeeTab = 'personal' | 'work' | 'links' | 'docs' | 'extra';
type EmployeeFeedbackStatus = 'DRAFT' | 'SCHEDULED' | 'APPLIED';
type EmployeeFeedbackTab = 'REGISTRO' | EmployeeFeedbackStatus;
type BusinessHistoryItem = {
  action: string;
  at: string;
  user_name: string;
  before: any | null;
  after: any | null;
  changes: Array<{ label: string; before: any; after: any }>;
};

interface EmployeeFeedbackFormDraft {
  feedback_id: number | null;
  feedback_template_id: string;
  notes: string;
  collaborator_notes: string;
  scheduled_for: string;
}

interface EmployeeFeedbackQuestionOption {
  id: number | null;
  label: string;
  order_index: number;
}

interface EmployeeFeedbackQuestion {
  id: number;
  title: string;
  description: string;
  question_type: string;
  required: boolean;
  order_index: number;
  options: EmployeeFeedbackQuestionOption[];
}

type DashboardLineTooltip = {
  visible: boolean;
  x: number;
  y: number;
  title: string;
  value: string;
};
type DashboardFilterKey = 'ALL' | 'COLLABORATOR' | 'THERMOMETER' | 'FEEDBACK';

@Component({
  selector: 'app-controlop',
  templateUrl: './controlop.component.html',
  styleUrls: ['./controlop.component.scss']
})
export class ControlopComponent implements OnInit {
  private readonly apiBase = `${API_BASE_URL}`;
  private readonly tabStorageKey = 'controlop.activeTab';

  tenantId: number | null = null;
  userId: number | null = null;
  isAdmin = false;

  activeTab: TabKey = 'dashboard';
  employeeTab: EmployeeTab = 'personal';
  readonly allTabs: TabKey[] = [
    'dashboard',
    'collaborators',
    'imports',
    'business',
    'forecast',
    'closing',
    'reports',
    'extras'
  ];
  allowedTabs: TabKey[] = [...this.allTabs];
  tabLabels: Record<TabKey, string> = {
    dashboard: 'Dashboard',
    collaborators: 'Colaboradores',
    imports: 'Importações',
    business: 'Detalhes do negócio',
    forecast: 'Forecast',
    closing: 'Fechamento',
    reports: 'Relatórios',
    extras: 'Adicionais'
  };
  tabIcons: Record<TabKey, string> = {
    dashboard: 'dashboard',
    collaborators: 'groups',
    imports: 'upload_file',
    business: 'inventory_2',
    forecast: 'stacked_line_chart',
    closing: 'task',
    reports: 'description',
    extras: 'tune'
  };

  dashboard = { total: 0, active: 0, on_leave: 0, terminated: 0, in_experience: 0 };

  employees: ControlDpEmployee[] = [];
  roles: ControlDpRole[] = [];
  occurrences: ControlDpOccurrence[] = [];
  allOccurrences: ControlDpOccurrence[] = [];
  occurrencesLoaded = false;
  occurrencesLoading = false;
  history: ControlDpHistory[] = [];
  attachments: ControlDpAttachment[] = [];
  imports: ControlDpImport[] = [];

  sectors: any[] = [];
  departments: any[] = [];
  locations: any[] = [];
  allowedDepartmentIds: number[] = [];
  allowedLocationIds: number[] = [];
  allowedDepartments: Array<{ id: number; name: string; sector_id?: number | null; sector_name?: string | null }> = [];
  controlopModuleEnabled = false;

  filterTerm = '';
  filterSectorId = '';
  filterDepartmentId = '';
  dashboardFilter: DashboardFilterKey = 'ALL';
  dashboardFilterTouched = false;
  dashboardFeedbackTemplateId = '';
  currentPage = 1;
  pageSize = 5;
  historyPage = 1;
  historyPageSize = 5;
  selectedEmployee: ControlDpEmployee | null = null;
  loading = false;

  showEmployeeModal = false;
  showOccurrenceModal = false;
  showHistoryModal = false;
  showRoleModal = false;
  showImportModal = false;
  showFilterModal = false;
  showBusinessModal = false;
  showBusinessViewModal = false;
  showBusinessDetails = false;
  showBusinessTimes = false;
  showBusinessProcesses = false;
  showBusinessSystems = false;
  showBusinessManuals = false;
  showForecastModal = false;
  showForecastHistoryModal = false;
  showClosingModal = false;
  showEmployeeFeedbackModal = false;

  employeeForm: any = this.defaultEmployeeForm();
  occurrenceForm: any = this.defaultOccurrenceForm();
  extraForm: any = this.defaultExtraForm();
  extraLoading = false;
  businessForm: any = this.defaultBusinessForm();
  businessProcessFile: File | null = null;
  businessMeoFile: File | null = null;
  businessAttachments: File[] = [];
  businessOtherProcessAttachments: File[] = [];
  businessManualAttachments: File[] = [];
  businessExistingProcessAttachment: any | null = null;
  businessExistingMeoAttachment: any | null = null;
  businessExistingOtherAttachments: any[] = [];
  businessExistingManualAttachments: any[] = [];
  businessActivities: any[] = [];
  forecasts: any[] = [];
  forecastSearchTerm = '';
  forecastValidationError = '';
  forecastFeedback = '';
  forecastFeedbackKind = '';
  forecastPageFeedback = '';
  forecastPageFeedbackKind = '';
  forecastForm: any = this.defaultForecastForm();
  forecastActivities: any[] = [];
  forecastHistory: any[] = [];
  editingForecastId: number | null = null;
  forecastExpandedIndex = 0;
  closings: any[] = [];
  closingForm: any = this.defaultClosingForm();
  closingValidationError = '';
  editingClosingId: number | null = null;
  reportBusinessDepartmentId = '';
  reportClosingDepartmentId = 'all';
  reportClosingDate = '';
  reportFeedback = '';
  reportFeedbackKind = '';
  selectedBusinessView: any | null = null;
  businessHistory: Record<number, BusinessHistoryItem[]> = {};
  editingBusinessId: number | null = null;
  editingBusinessSnapshot: any | null = null;
  showBusinessHistoryModal = false;
  selectedBusinessHistory: BusinessHistoryItem[] = [];
  businessModelOptions = ['Ticket', 'PA', 'Ticket/PA'];
  businessClientSystems: string[] = [];
  businessAcertSystems: string[] = [];
  businessSupplierSystems: string[] = [];
  businessClientInput = '';
  businessAcertInput = '';
  businessSupplierInput = '';
  roleForm = { name: '', base_salary: '' };
  editingRole: ControlDpRole | null = null;
  occurrenceAttachmentFile: File | null = null;
  occurrenceAttachmentName = '';
  occurrenceAttachmentDragging = false;
  employeeFeedbackTarget: ControlDpEmployee | null = null;
  employeeFeedbackTab: EmployeeFeedbackTab = 'REGISTRO';
  employeeFeedbackTabs: Array<{ value: EmployeeFeedbackTab; label: string; icon: string }> = [
    { value: 'REGISTRO', label: 'Registro', icon: 'edit_note' },
    { value: 'DRAFT', label: 'Rascunho', icon: 'drafts' },
    { value: 'SCHEDULED', label: 'Agendados', icon: 'event' },
    { value: 'APPLIED', label: 'Aplicados', icon: 'task_alt' }
  ];
  employeeFeedbackLoading = false;
  employeeFeedbackQuestionsLoading = false;
  employeeFeedbackSaving = false;
  employeeFeedbackError = '';
  employeeFeedbackRecords: ControlDpEmployeeFeedback[] = [];
  employeeFeedbackQuestions: EmployeeFeedbackQuestion[] = [];
  employeeFeedbackAnswers: Record<string, any> = {};
  employeeFeedbackForm: EmployeeFeedbackFormDraft = this.defaultEmployeeFeedbackForm();
  feedbackTemplates: ControlDpFeedbackTemplate[] = [];
  feedbackTemplatesLoading = false;
  private employeeFeedbackQuestionRequestId = 0;

  importType = 'EMPLOYEES';
  importFileName = '';
  importPreviewRows: Record<string, any>[] = [];
  importHeaders: string[] = [];
  importLoading = false;
  importFeedback = '';
  importErrorRows: Array<{ row_index: number; __error?: string }> = [];
  exportLoading = false;

  importCards = [
    { key: 'EMPLOYEES', title: 'Importar colaboradores', description: 'Base principal de colaboradores.' },
    { key: 'ABSENCES', title: 'Importar faltas', description: 'Registros de ausência por colaborador.' },
    { key: 'LATES', title: 'Importar atrasos', description: 'Registros de atrasos por colaborador.' }
  ];
  dashboardLineTooltip: DashboardLineTooltip = {
    visible: false,
    x: 0,
    y: 0,
    title: '',
    value: ''
  };
  dashboardPeriodMonths = 12;
  readonly dashboardPeriodOptions = [6, 12, 24];
  private admissionsChartCacheSignature = '';
  private admissionsChartCache: ChartData<'line'> = { labels: [], datasets: [] };
  dashboardLineChartOptions: ChartConfiguration<'line'>['options'] = {
    responsive: true,
    maintainAspectRatio: false,
    interaction: { mode: 'index', intersect: false },
    animation: { duration: 420 },
    plugins: {
      legend: { display: false },
      tooltip: {
        backgroundColor: '#15385f',
        titleColor: '#ffffff',
        bodyColor: '#e9f1ff',
        displayColors: true,
        cornerRadius: 10,
        padding: 10
      }
    },
    scales: {
      x: {
        grid: { display: false },
        ticks: {
          color: '#7f92aa',
          font: { size: 12, weight: 'bold' },
          maxRotation: 0,
          minRotation: 0,
          autoSkip: true,
          maxTicksLimit: 12
        }
      },
      y: {
        beginAtZero: true,
        grid: { color: '#e7eef8' },
        ticks: {
          color: '#7f92aa',
          font: { size: 11, weight: 600 },
          precision: 0
        }
      }
    }
  };
  private readonly dashboardDepartmentPalette = ['#22c2be', '#55d4d1', '#86dce2', '#a8dce6', '#c6e7ef', '#d9eef5'];
  private readonly moodOptions = [
    { id: 'excellent', label: 'Excelente', emoji: '😄' },
    { id: 'good', label: 'Bom', emoji: '🙂' },
    { id: 'neutral', label: 'Neutro', emoji: '😐' },
    { id: 'sad', label: 'Triste', emoji: '☹️' },
    { id: 'terrible', label: 'Péssimo', emoji: '😢' }
  ];
  moodSummaryRows: Array<{ id: string; label: string; emoji: string; count: number; percent: number }> = this.moodOptions.map((item) => ({
    id: item.id,
    label: item.label,
    emoji: item.emoji,
    count: 0,
    percent: 0
  }));
  moodSummaryTotal = 0;
  moodSummaryLoading = false;
  moodPeriodMonths = 3;
  readonly moodPeriodOptions = [3, 6, 12];
  readonly dashboardFilterOptions: Array<{ value: DashboardFilterKey; label: string }> = [
    { value: 'ALL', label: 'Todos os dashboards' },
    { value: 'COLLABORATOR', label: 'Dashboard colaborador' },
    { value: 'THERMOMETER', label: 'Dashboard termômetro' },
    { value: 'FEEDBACK', label: 'Dashboard feedback' }
  ];
  moodMonthlyRows: Array<{ month_key: string; label: string; count: number; unique_respondents: number }> = [];
  moodGenderRows: Array<{ key: string; label: string; count: number; percent: number }> = [];
  moodDepartmentRows: Array<{
    label: string;
    count: number;
    percent: number;
    positive_count: number;
    neutral_count: number;
    negative_count: number;
  }> = [];
  moodUniqueRespondents = 0;
  feedbackDashboardLoading = false;
  feedbackDashboard: ControlDpFeedbackDashboard | null = null;

  downloadImportTemplate(type: string) {
    const template = this.getTemplateDefinition(type);
    const data = [template.headers, template.sample];
    const worksheet = XLSX.utils.aoa_to_sheet(data);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, 'Modelo');
    XLSX.writeFile(workbook, template.fileName);
  }

  maritalStatusOptions = [
    'Solteiro(a)',
    'Casado(a)',
    'União estável',
    'Divorciado(a)',
    'Viúvo(a)',
    'Separado(a)'
  ];
  contractTypeOptions = [
    'CLT',
    'PJ',
    'Estágio',
    'Temporário',
    'Aprendiz',
    'Terceirizado',
    'Outro'
  ];
  bankAccountTypes = ['Corrente', 'Poupança', 'Salário', 'Outro'];
  raceOptions = ['Branca', 'Preta', 'Parda', 'Amarela', 'Indígena', 'Não informado'];
  educationLevelOptions = [
    'Ensino fundamental incompleto',
    'Ensino fundamental completo',
    'Ensino médio incompleto',
    'Ensino médio completo',
    'Ensino superior incompleto',
    'Ensino superior completo',
    'Pós-graduação',
    'Mestrado',
    'Doutorado'
  ];

  constructor(
    private service: ControlDpService,
    private controlopService: ControlopService,
    private session: UserSessionService,
    private orgService: OrgStructureService,
    private userService: UserService,
    private moodService: MoodService,
    private controlquestion: ControlQuestionService
  ) {}

  ngOnInit(): void {
    const savedTab = this.getSavedTab();
    if (savedTab) {
      this.activeTab = savedTab;
    }
    const user = this.session.getUser();
    this.tenantId = user?.tenant_id ?? null;
    this.userId = user?.id ?? null;
    this.isAdmin = this.resolveIsAdmin(user);
    this.controlopModuleEnabled = this.resolveControlopModule(user);
    if (this.activeTab === 'imports' && !this.isAdmin) {
      this.activeTab = 'dashboard';
    }
    if (this.tenantId) {
      this.loadControlopScope();
      this.loadDashboard();
      this.loadEmployees();
      this.loadRoles();
      this.loadImports();
      this.loadOrgStructure();
      if (this.activeTab === 'business') {
        this.loadBusinessActivities();
      }
      if (this.activeTab === 'forecast') {
        this.loadForecasts();
        this.loadBusinessActivities();
      }
      if (this.activeTab === 'closing') {
        this.loadClosings();
      }
    }
  }

  loadDashboard() {
    if (!this.tenantId) return;
    this.service.getDashboard(this.tenantId).subscribe({
      next: (summary) => (this.dashboard = summary),
      error: () => (this.dashboard = { total: 0, active: 0, on_leave: 0, terminated: 0, in_experience: 0 })
    });
  }

  loadMoodThermometer(months = this.moodPeriodMonths) {
    if (!this.tenantId) {
      this.moodSummaryTotal = 0;
      this.moodSummaryRows = this.moodOptions.map((item) => ({ ...item, count: 0, percent: 0 }));
      this.moodMonthlyRows = [];
      this.moodGenderRows = [];
      this.moodDepartmentRows = [];
      this.moodUniqueRespondents = 0;
      return;
    }
    const scopedUserIds = this.scopedMoodUserIds;
    const scopedDepartmentIds = this.scopedMoodDepartmentIds;
    this.moodSummaryLoading = true;
    this.moodService.getScopedAnalytics(this.tenantId, scopedUserIds, months, scopedDepartmentIds).subscribe({
      next: (result) => {
        const sourceRows = Array.isArray(result?.moods) ? result.moods : [];
        const counts = new Map<string, { count: number; percent: number }>();
        sourceRows.forEach((row: any) => {
          counts.set(String(row?.mood || ''), {
            count: Number(row?.count || 0),
            percent: Number(row?.percent || 0)
          });
        });
        this.moodSummaryTotal = Number(result?.total_responses || 0);
        this.moodSummaryRows = this.moodOptions.map((item) => {
          const found = counts.get(item.id);
          return {
            id: item.id,
            label: item.label,
            emoji: item.emoji,
            count: Number(found?.count || 0),
            percent: Number(found?.percent || 0)
          };
        });
        this.moodMonthlyRows = Array.isArray(result?.monthly) ? result.monthly : [];
        this.moodGenderRows = Array.isArray(result?.by_gender) ? result.by_gender : [];
        this.moodDepartmentRows = Array.isArray(result?.by_department) ? result.by_department : [];
        this.moodUniqueRespondents = Number(result?.unique_respondents || 0);
        this.moodSummaryLoading = false;
      },
      error: () => {
        this.moodSummaryTotal = 0;
        this.moodSummaryRows = this.moodOptions.map((item) => ({ ...item, count: 0, percent: 0 }));
        this.moodMonthlyRows = [];
        this.moodGenderRows = [];
        this.moodDepartmentRows = [];
        this.moodUniqueRespondents = 0;
        this.moodSummaryLoading = false;
      }
    });
  }

  loadEmployees() {
    if (!this.tenantId) return;
    this.loading = true;
    this.service.listEmployees(this.tenantId).subscribe({
      next: (rows) => {
        this.employees = rows || [];
        this.loadMoodThermometer(this.moodPeriodMonths);
        this.loading = false;
      },
      error: () => {
        this.employees = [];
        this.loadMoodThermometer(this.moodPeriodMonths);
        this.loading = false;
      }
    });
  }

  loadRoles() {
    if (!this.tenantId) return;
    this.service.listRoles(this.tenantId).subscribe({
      next: (rows) => {
        this.roles = rows || [];
        if (this.showEmployeeModal && this.employeeForm.role_id && this.isSalaryEmpty(this.employeeForm.salary)) {
          this.onRoleChange();
        }
      },
      error: () => (this.roles = [])
    });
  }

  loadImports() {
    if (!this.tenantId) return;
    this.service.listImports(this.tenantId).subscribe({
      next: (rows) => (this.imports = rows || []),
      error: () => (this.imports = [])
    });
  }

  loadControlopScope() {
    if (!this.tenantId || !this.userId) {
      return;
    }
    const userId = this.userId;
    this.controlopService.getPermissions(this.tenantId, this.userId).subscribe({
      next: (payload) => {
        const departments = payload?.departments || [];
        const locations = payload?.locations || [];
        const tabs = payload?.tabs || [];
        if (departments.length || locations.length || tabs.length) {
          this.applyControlopScope(departments, locations);
          this.applyControlopTabs(tabs);
          return;
        }
        this.userService.getUserControlopAssignments(userId).subscribe({
          next: (fallback) => {
            this.applyControlopScope(fallback?.departments || [], fallback?.locations || []);
            this.applyControlopTabs(fallback?.tabs || []);
          },
          error: () => {
            this.clearControlopScope();
          }
        });
      },
      error: () => {
        this.userService.getUserControlopAssignments(userId).subscribe({
          next: (fallback) => {
            this.applyControlopScope(fallback?.departments || [], fallback?.locations || []);
            this.applyControlopTabs(fallback?.tabs || []);
          },
          error: () => {
            this.clearControlopScope();
          }
        });
      }
    });
  }

  loadBusinessActivities() {
    if (!this.tenantId) {
      return;
    }
    this.controlopService.listActivities(this.tenantId).subscribe({
      next: (rows) => (this.businessActivities = rows || []),
      error: () => (this.businessActivities = [])
    });
  }

  loadForecasts() {
    if (!this.tenantId) {
      return;
    }
    this.controlopService.listForecasts(this.tenantId).subscribe({
      next: (rows) => (this.forecasts = rows || []),
      error: () => (this.forecasts = [])
    });
  }

  loadClosings() {
    if (!this.tenantId) {
      return;
    }
    this.controlopService.listClosings(this.tenantId).subscribe({
      next: (rows) => (this.closings = rows || []),
      error: () => (this.closings = [])
    });
  }

  private applyControlopScope(departments: any[], locations: any[]) {
    this.allowedDepartmentIds = (departments || [])
      .map((dept: any) => Number(dept?.id ?? dept?.department_id))
      .filter((id: number) => Number.isFinite(id) && id > 0);
    this.allowedLocationIds = (locations || [])
      .map((loc: any) => Number(loc?.id ?? loc?.location_id))
      .filter((id: number) => Number.isFinite(id) && id > 0);
    this.allowedDepartments = departments.map((dept: any) => ({
      id: Number(dept.id),
      name: dept.name,
      sector_id: dept.sector_id ?? null,
      sector_name: dept.sector_name ?? null
    }));
  }

  private applyControlopTabs(tabs: string[]) {
    const filtered = (tabs || [])
      .map((tab) => String(tab))
      .filter((tab): tab is TabKey => this.allTabs.includes(tab as TabKey));
    this.allowedTabs = filtered.length ? filtered : [...this.allTabs];
    this.ensureActiveTabAllowed();
  }

  private clearControlopScope() {
    this.allowedDepartmentIds = [];
    this.allowedLocationIds = [];
    this.allowedDepartments = [];
  }

  loadOrgStructure() {
    if (!this.tenantId) return;
    this.orgService.getSectors(this.tenantId).subscribe({ next: (rows) => (this.sectors = rows || []) });
    this.orgService.getDepartments(this.tenantId).subscribe({ next: (rows) => (this.departments = rows || []) });
    this.orgService.getLocations(this.tenantId).subscribe({ next: (rows) => (this.locations = rows || []) });
  }

  private resolveControlopModule(user: any) {
    const modules = Array.isArray(user?.modules) ? user.modules : [];
    return modules.some((module: any) => {
      const key = typeof module === 'string' ? module : module?.module_key;
      const canView = typeof module === 'string' ? true : !!module?.can_view;
      return key === 'controlop' && canView;
    });
  }

  private resolveIsAdmin(user: any) {
    const name = String(user?.role_name || user?.role?.name || '').toLowerCase();
    return name === 'admin';
  }

  get hasControlopScope() {
    return this.controlopModuleEnabled && (this.allowedDepartmentIds.length > 0 || this.allowedLocationIds.length > 0);
  }

  get allowedDepartmentLabels() {
    if (!this.allowedDepartmentIds.length) {
      return [];
    }
    const seen = new Set<number>();
    const byId = new Map(this.departments.map((dept) => [Number(dept.id), dept]));
    return this.allowedDepartmentIds
      .map((deptId) => {
        const numericId = Number(deptId);
        if (!Number.isFinite(numericId) || seen.has(numericId)) {
          return null;
        }
        seen.add(numericId);
        const dept = byId.get(numericId) || this.allowedDepartments.find((item) => item.id === numericId);
        if (!dept) {
          return `Departamento ${numericId}`;
        }
        const sectorName = dept.sector_name || '';
        return sectorName ? `${sectorName} - ${dept.name}` : dept.name;
      })
      .filter((label): label is string => !!label);
  }

  get availableDepartments() {
    if (!this.employeeForm.sector_id) {
      return this.filterDepartmentsByScope(this.departments);
    }
    return this.filterDepartmentsByScope(
      this.departments.filter((dept) => String(dept.sector_id) === String(this.employeeForm.sector_id))
    );
  }

  get availableFilterDepartments() {
    if (!this.filterSectorId) {
      return this.filterDepartmentsByScope(this.departments);
    }
    return this.filterDepartmentsByScope(
      this.departments.filter((dept) => String(dept.sector_id) === String(this.filterSectorId))
    );
  }

  get availableLocations() {
    if (!this.employeeForm.department_id) {
      return this.filterLocationsByScope(this.locations);
    }
    return this.filterLocationsByScope(
      this.locations.filter((loc) => String(loc.department_id) === String(this.employeeForm.department_id))
    );
  }

  get scopedDepartmentsForExtras() {
    return this.filterDepartmentsByScope(this.departments);
  }

  get scopedDepartmentsForControlopForms() {
    return this.filterDepartmentsByScope(this.departments);
  }

  get scopedLocationsForExtras() {
    return this.filterLocationsByScope(this.locations);
  }

  get scopedSectors() {
    if (!this.hasControlopScope) {
      return this.sectors;
    }
    const allowedDepartments = new Set(this.allowedDepartmentIds);
    const sectorIds = new Set(
      this.departments
        .filter((dept) => allowedDepartments.has(Number(dept.id)))
        .map((dept) => Number(dept.sector_id))
    );
    return this.sectors.filter((sector) => sectorIds.has(Number(sector.id)));
  }

  private filterDepartmentsByScope(items: any[]) {
    if (!this.hasControlopScope) {
      return items;
    }
    return items.filter((dept) => this.allowedDepartmentIds.includes(Number(dept.id)));
  }

  private filterLocationsByScope(items: any[]) {
    if (!this.hasControlopScope) {
      return items;
    }
    const allowedDepartments = new Set(this.allowedDepartmentIds);
    return items.filter((loc) => {
      const locId = Number(loc.id);
      if (this.allowedLocationIds.length && this.allowedLocationIds.includes(locId)) {
        return true;
      }
      if (allowedDepartments.size && allowedDepartments.has(Number(loc.department_id))) {
        return true;
      }
      return false;
    });
  }

  private matchesDepartmentFilter(departmentId: any) {
    if (!this.filterSectorId && !this.filterDepartmentId) {
      return true;
    }
    if (!departmentId) {
      return false;
    }
    const normalizedId = String(departmentId);
    if (this.filterDepartmentId && normalizedId !== String(this.filterDepartmentId)) {
      return false;
    }
    if (this.filterSectorId) {
      const department = this.departments.find((dept) => String(dept.id) === normalizedId);
      if (!department || String(department.sector_id) !== String(this.filterSectorId)) {
        return false;
      }
    }
    return true;
  }

  private getForecastActivityDepartmentId(activity: any) {
    if (!activity) {
      return null;
    }
    if (activity.department_id) {
      return activity.department_id;
    }
    if (!activity.activity_id) {
      return null;
    }
    const match = this.businessActivities.find((item) => String(item.id) === String(activity.activity_id));
    return match?.department_id ?? null;
  }

  onSectorChange() {
    this.employeeForm.department_id = '';
    this.employeeForm.location_id = '';
  }

  onDepartmentChange() {
    this.employeeForm.location_id = '';
  }

  onFilterSectorChange() {
    this.filterDepartmentId = '';
  }

  getAgeLabel() {
    if (!this.employeeForm.birth_date) {
      return '';
    }
    const birth = new Date(this.employeeForm.birth_date);
    if (Number.isNaN(birth.getTime())) {
      return '';
    }
    const today = new Date();
    let age = today.getFullYear() - birth.getFullYear();
    const monthDiff = today.getMonth() - birth.getMonth();
    if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < birth.getDate())) {
      age -= 1;
    }
    return String(age);
  }

  get filteredEmployees() {
    let filtered = this.scopedEmployees;
    if (this.filterTerm) {
      const term = this.filterTerm.toLowerCase();
      filtered = filtered.filter((emp) =>
        [emp.full_name, emp.cpf, emp.role_name].some((value) => (value || '').toLowerCase().includes(term))
      );
    }
    return filtered;
  }

  get filteredForecasts() {
    let filtered = this.forecasts;
    if (this.forecastSearchTerm) {
      const term = this.forecastSearchTerm.toLowerCase();
      filtered = filtered.filter((item) => {
        const titleMatch = (item.title || '').toLowerCase().includes(term);
        const activityMatch = (item.activities || []).some((activity: any) =>
          (activity.activity_title || '').toLowerCase().includes(term)
        );
        return titleMatch || activityMatch;
      });
    }
    if (!this.filterSectorId && !this.filterDepartmentId) {
      return filtered;
    }
    return filtered.filter((forecast) => {
      const activities = forecast.activities || [];
      return activities.some((activity: any) => {
        const departmentId = this.getForecastActivityDepartmentId(activity);
        return this.matchesDepartmentFilter(departmentId);
      });
    });
  }

  get filteredBusinessActivities() {
    if (!this.filterSectorId && !this.filterDepartmentId) {
      return this.businessActivities;
    }
    return this.businessActivities.filter((activity) => this.matchesDepartmentFilter(activity?.department_id));
  }

  get filteredClosings() {
    if (!this.filterSectorId && !this.filterDepartmentId) {
      return this.closings;
    }
    return this.closings.filter((closing) => this.matchesDepartmentFilter(closing?.department_id));
  }

  get scopedEmployees() {
    let filtered = this.employees;
    if (this.hasControlopScope) {
      if (!this.allowedDepartmentIds.length && !this.allowedLocationIds.length) {
        return [];
      }
      const allowedDepartments = new Set(this.allowedDepartmentIds);
      const allowedLocations = new Set(this.allowedLocationIds);
      filtered = filtered.filter((emp) => {
        const deptId = Number(emp.department_id);
        const locId = Number(emp.location_id);
        if (allowedLocations.size && allowedLocations.has(locId)) {
          return true;
        }
        if (allowedDepartments.size && allowedDepartments.has(deptId)) {
          return true;
        }
        return false;
      });
    }
    if (this.filterSectorId) {
      filtered = filtered.filter((emp) => String(emp.sector_id || '') === String(this.filterSectorId));
    }
    if (this.filterDepartmentId) {
      filtered = filtered.filter((emp) => String(emp.department_id || '') === String(this.filterDepartmentId));
    }
    return filtered;
  }

  get dashboardView() {
    const summary = { total: 0, active: 0, on_leave: 0, terminated: 0, in_experience: 0 };
    this.scopedEmployees.forEach((emp) => {
      summary.total += 1;
      const status = String(emp.status || '').toLowerCase();
      if (status === 'active') summary.active += 1;
      if (status === 'on_leave') summary.on_leave += 1;
      if (status === 'terminated') summary.terminated += 1;
      if (status === 'in_experience') summary.in_experience += 1;
    });
    return summary;
  }

  get experienceSummary() {
    const summary = { exp45: 0, exp90: 0 };
    const today = new Date();
    this.scopedEmployees.forEach((emp) => {
      if (String(emp.status || '').toLowerCase() !== 'active') {
        return;
      }
      if (!emp.hired_at) {
        return;
      }
      const hired = new Date(emp.hired_at);
      if (Number.isNaN(hired.getTime())) {
        return;
      }
      const limit45 = emp.experience_45_days ? new Date(emp.experience_45_days) : this.addDays(hired, 45);
      const limit90 = emp.experience_90_days ? new Date(emp.experience_90_days) : this.addDays(hired, 90);
      if (!Number.isNaN(limit45.getTime()) && today <= limit45) {
        summary.exp45 += 1;
        return;
      }
      if (!Number.isNaN(limit90.getTime()) && today <= limit90) {
        summary.exp90 += 1;
      }
    });
    return summary;
  }

  get admissionsByMonth() {
    const monthNames = ['Jan', 'Fev', 'Mar', 'Abr', 'Mai', 'Jun', 'Jul', 'Ago', 'Set', 'Out', 'Nov', 'Dez'];
    const now = new Date();
    const buckets: Array<{ key: string; label: string; count: number }> = [];
    const period = Math.max(this.dashboardPeriodMonths, 1);
    for (let i = period - 1; i >= 0; i -= 1) {
      const date = new Date(now.getFullYear(), now.getMonth() - i, 1);
      const key = `${date.getFullYear()}-${date.getMonth()}`;
      const label = `${monthNames[date.getMonth()]} ${String(date.getFullYear()).slice(-2)}`;
      buckets.push({ key, label, count: 0 });
    }
    const bucketMap = new Map(buckets.map((bucket) => [bucket.key, bucket]));
    this.scopedEmployees.forEach((emp) => {
      if (!emp.hired_at) {
        return;
      }
      const hired = new Date(emp.hired_at);
      if (Number.isNaN(hired.getTime())) {
        return;
      }
      const key = `${hired.getFullYear()}-${hired.getMonth()}`;
      const bucket = bucketMap.get(key);
      if (bucket) {
        bucket.count += 1;
      }
    });
    return buckets;
  }

  get admissionsMax() {
    return this.admissionsByMonth.reduce((max, item) => Math.max(max, item.count), 0);
  }

  get admissionsTotal() {
    return this.admissionsByMonth.reduce((sum, item) => sum + item.count, 0);
  }

  get sectorDistribution() {
    return this.aggregateDistribution(this.scopedEmployees, 'sector_name', 'Sem setor');
  }

  get sectorDistributionMax() {
    return this.sectorDistribution.reduce((max, item) => Math.max(max, item.count), 0);
  }

  get departmentDistribution() {
    return this.aggregateDistribution(this.scopedEmployees, 'department_name', 'Sem departamento');
  }

  get departmentDistributionMax() {
    return this.departmentDistribution.reduce((max, item) => Math.max(max, item.count), 0);
  }

  get hiresLast30Days() {
    const now = new Date();
    const cutoff = new Date(now.getFullYear(), now.getMonth(), now.getDate() - 30);
    return this.scopedEmployees.filter((emp) => {
      if (!emp.hired_at) return false;
      const hired = new Date(emp.hired_at);
      return !Number.isNaN(hired.getTime()) && hired >= cutoff;
    }).length;
  }

  get averageTenureLabel() {
    const now = new Date();
    const tenures = this.scopedEmployees
      .map((emp) => (emp.hired_at ? new Date(emp.hired_at) : null))
      .filter((date) => date && !Number.isNaN(date.getTime())) as Date[];
    if (!tenures.length) {
      return '—';
    }
    const totalDays = tenures.reduce((sum, date) => sum + this.diffInDays(date, now), 0);
    const avgDays = Math.round(totalDays / tenures.length);
    const months = Math.floor(avgDays / 30);
    if (months < 1) {
      return `${avgDays} dias`;
    }
    return `${months} meses`;
  }

  get activeRateLabel() {
    const total = this.scopedEmployees.length;
    if (!total) {
      return '0%';
    }
    const rate = Math.round((this.dashboardView.active / total) * 100);
    return `${rate}%`;
  }

  get scopedMoodUserIds() {
    const ids = new Set<number>();
    this.scopedEmployees.forEach((employee) => {
      const raw = Number((employee as any)?.user_id || 0);
      if (Number.isFinite(raw) && raw > 0) {
        ids.add(raw);
      }
    });
    return Array.from(ids);
  }

  get scopedMoodDepartmentIds() {
    const ids = new Set<number>();
    this.scopedEmployees.forEach((employee) => {
      const raw = Number(employee?.department_id || 0);
      if (Number.isFinite(raw) && raw > 0) {
        ids.add(raw);
      }
    });
    return Array.from(ids);
  }

  get scopedMoodEmployeeTotal() {
    return this.scopedEmployees.length;
  }

  get moodResponseRateLabel() {
    const total = this.scopedMoodEmployeeTotal;
    if (!total) {
      return '0%';
    }
    const rate = Number(((this.moodUniqueRespondents / total) * 100).toFixed(1));
    return `${String(rate).replace('.', ',')}%`;
  }

  get moodMonthlyMax() {
    return this.moodMonthlyRows.reduce((max, item) => Math.max(max, Number(item.count || 0)), 0);
  }

  get moodGenderMax() {
    return this.moodGenderRows.reduce((max, item) => Math.max(max, Number(item.count || 0)), 0);
  }

  get moodDepartmentMax() {
    return this.moodDepartmentRows.reduce((max, item) => Math.max(max, Number(item.count || 0)), 0);
  }

  getDepartmentMoodSegmentPercent(
    item: { count: number; positive_count: number; neutral_count: number; negative_count: number },
    segment: 'positive' | 'neutral' | 'negative'
  ) {
    const total = Number(item?.count || 0);
    if (!total) {
      return 0;
    }
    const value = segment === 'positive'
      ? Number(item?.positive_count || 0)
      : segment === 'neutral'
        ? Number(item?.neutral_count || 0)
        : Number(item?.negative_count || 0);
    return Number(((value / total) * 100).toFixed(1));
  }

  moodColor(moodId: string) {
    const palette: Record<string, string> = {
      excellent: '#11b981',
      good: '#22c6be',
      neutral: '#f2c94c',
      sad: '#fb923c',
      terrible: '#ef4444'
    };
    return palette[String(moodId || '').toLowerCase()] || '#22c6be';
  }

  get moodDonutStyle() {
    const rows = (this.moodSummaryRows || []).filter((row) => Number(row.percent || 0) > 0);
    if (!rows.length) {
      return 'conic-gradient(#e7eef8 0% 100%)';
    }
    let current = 0;
    const parts = rows.map((row) => {
      const start = current;
      current += Number(row.percent || 0);
      return `${this.moodColor(row.id)} ${start}% ${Math.min(current, 100)}%`;
    });
    if (current < 100) {
      parts.push(`#eef3fb ${current}% 100%`);
    }
    return `conic-gradient(${parts.join(', ')})`;
  }

  get moodGenderDonutStyle() {
    const rows = (this.moodGenderRows || []).filter((row) => Number(row.percent || 0) > 0);
    if (!rows.length) {
      return 'conic-gradient(#e7eef8 0% 100%)';
    }
    let current = 0;
    const parts = rows.map((row) => {
      const start = current;
      current += Number(row.percent || 0);
      return `${this.moodGenderColor(row.key)} ${start}% ${Math.min(current, 100)}%`;
    });
    if (current < 100) {
      parts.push(`#eef3fb ${current}% 100%`);
    }
    return `conic-gradient(${parts.join(', ')})`;
  }

  moodGenderColor(key: string) {
    const normalized = String(key || '').toUpperCase();
    if (normalized === 'F') {
      return '#5C83FF';
    }
    if (normalized === 'M') {
      return '#21C2BA';
    }
    if (normalized === 'OTHER') {
      return '#34D399';
    }
    if (normalized === 'NOT_INFORMED') {
      return '#F6C85F';
    }
    return '#9CA3AF';
  }

  get moodTrendMaxValue() {
    return Math.max(
      ...this.moodMonthlyRows.map((item) => Math.max(Number(item.count || 0), Number(item.unique_respondents || 0))),
      1
    );
  }

  moodTrendYByValue(value: number) {
    const chartTop = 26;
    const chartBottom = 186;
    const maxValue = this.moodTrendMaxValue || 1;
    const y = chartBottom - (Number(value || 0) / maxValue) * (chartBottom - chartTop);
    return Number(y.toFixed(2));
  }

  get moodTrendDots() {
    const points: Array<{ x: number; y: number; value: number; label: string }> = [];
    const values = this.moodMonthlyRows || [];
    if (!values.length) {
      return points;
    }
    const widthStart = 36;
    const widthEnd = 620;
    const step = values.length > 1 ? (widthEnd - widthStart) / (values.length - 1) : 0;
    values.forEach((item, index) => {
      const count = Number(item?.count || 0);
      const x = widthStart + step * index;
      const y = this.moodTrendYByValue(count);
      points.push({
        x: Number(x.toFixed(2)),
        y,
        value: count,
        label: String(item?.label || '')
      });
    });
    return points;
  }

  get moodTrendLinePointList() {
    return this.moodTrendDots.map((point) => `${point.x},${point.y}`).join(' ');
  }

  get moodTrendRespondentDots() {
    const points: Array<{ x: number; y: number; value: number; label: string }> = [];
    const values = this.moodMonthlyRows || [];
    if (!values.length) {
      return points;
    }
    const widthStart = 36;
    const widthEnd = 620;
    const step = values.length > 1 ? (widthEnd - widthStart) / (values.length - 1) : 0;
    values.forEach((item, index) => {
      const count = Number(item?.unique_respondents || 0);
      const x = widthStart + step * index;
      const y = this.moodTrendYByValue(count);
      points.push({
        x: Number(x.toFixed(2)),
        y,
        value: count,
        label: String(item?.label || '')
      });
    });
    return points;
  }

  get moodTrendRespondentPointList() {
    return this.moodTrendRespondentDots.map((point) => `${point.x},${point.y}`).join(' ');
  }

  get moodTrendAreaPath() {
    const dots = this.moodTrendDots;
    if (!dots.length) {
      return '';
    }
    const chartBottom = 186;
    const first = dots[0];
    const lines = dots.map((point) => `L ${point.x} ${point.y}`).join(' ');
    return `M ${first.x} ${chartBottom} ${lines} L ${dots[dots.length - 1].x} ${chartBottom} Z`;
  }

  setMoodPeriod(months: number) {
    this.moodPeriodMonths = months;
    this.loadMoodThermometer(months);
  }

  setDashboardPeriod(months: number) {
    this.dashboardPeriodMonths = months;
  }

  get dashboardInactiveCount() {
    return Math.max(this.dashboardView.total - this.dashboardView.active, 0);
  }

  get dashboardInactiveRateNumber() {
    const total = this.dashboardView.total;
    if (!total) {
      return 0;
    }
    return Number(((this.dashboardInactiveCount / total) * 100).toFixed(1));
  }

  get dashboardInactiveRateLabel() {
    return `${String(this.dashboardInactiveRateNumber.toFixed(1)).replace('.', ',')}%`;
  }

  get dashboardDepartmentRows() {
    return (this.departmentDistribution || []).map((row, index) => ({
      ...row,
      color: this.dashboardDepartmentPalette[index % this.dashboardDepartmentPalette.length]
    }));
  }

  get dashboardDepartmentDonutStyle() {
    if (!this.dashboardDepartmentRows.length) {
      return 'conic-gradient(#e7eef8 0% 100%)';
    }
    let current = 0;
    const parts = this.dashboardDepartmentRows.map((row) => {
      const start = current;
      current += row.percent;
      return `${row.color} ${start}% ${Math.min(current, 100)}%`;
    });
    if (current < 100) {
      parts.push(`#eef3fb ${current}% 100%`);
    }
    return `conic-gradient(${parts.join(', ')})`;
  }

  get dashboardDepartmentTop() {
    return this.dashboardDepartmentRows[0] || {
      label: 'Sem dados',
      count: 0,
      percent: 0,
      color: '#e7eef8'
    };
  }

  get admissionsSeriesValues() {
    return this.admissionsByMonth.map((item) => item.count);
  }

  get admissionsTrendSeriesValues() {
    const values = this.admissionsSeriesValues;
    if (!values.length) {
      return [];
    }
    return values.map((_, index) => {
      const current = values[index] || 0;
      const prev = values[index - 1] || current;
      const next = values[index + 1] || current;
      return Number(((prev + current + next) / 3).toFixed(2));
    });
  }

  get admissionsAveragePerMonth() {
    const values = this.admissionsSeriesValues;
    if (!values.length) {
      return 0;
    }
    const total = values.reduce((sum, value) => sum + value, 0);
    return Number((total / values.length).toFixed(2));
  }

  get admissionsAveragePerMonthLabel() {
    return this.admissionsAveragePerMonth.toLocaleString('pt-BR', {
      minimumFractionDigits: 1,
      maximumFractionDigits: 1
    });
  }

  get admissionsLineChartData(): ChartData<'line'> {
    const labels = this.admissionsByMonth.map((item) => item.label);
    const admissions = this.admissionsSeriesValues;
    const trend = this.admissionsTrendSeriesValues;
    const avg = this.admissionsAveragePerMonth;
    const signature = JSON.stringify({ labels, admissions, trend, avg });
    if (signature === this.admissionsChartCacheSignature) {
      return this.admissionsChartCache;
    }
    this.admissionsChartCacheSignature = signature;
    this.admissionsChartCache = {
      labels,
      datasets: [
        {
          label: 'Admissões',
          data: admissions,
          borderColor: '#1ec2bf',
          backgroundColor: 'rgba(30, 194, 191, 0.2)',
          borderWidth: 3,
          pointBackgroundColor: '#1ec2bf',
          pointBorderColor: '#ffffff',
          pointBorderWidth: 2,
          pointRadius: 5,
          pointHoverRadius: 6,
          fill: true,
          tension: 0.35
        },
        {
          label: 'Tendência',
          data: trend,
          borderColor: '#f0cb59',
          borderWidth: 3,
          pointBackgroundColor: '#f0cb59',
          pointBorderColor: '#ffffff',
          pointBorderWidth: 2,
          pointRadius: 4,
          pointHoverRadius: 5,
          fill: false,
          tension: 0.35
        },
        {
          label: `Média ${this.admissionsAveragePerMonthLabel}/mês`,
          data: labels.map(() => avg),
          borderColor: '#8ea2bf',
          borderWidth: 1.5,
          borderDash: [6, 4],
          pointRadius: 0,
          pointHoverRadius: 0,
          fill: false,
          tension: 0
        }
      ]
    };
    return this.admissionsChartCache;
  }

  get dashboardLineMaxValue() {
    return Math.max(...this.admissionsSeriesValues, ...this.admissionsTrendSeriesValues, 1);
  }

  dashboardLinePointList(values: number[]) {
    return this.dashboardLineDots(values)
      .map((point) => `${point.x},${point.y}`)
      .join(' ');
  }

  dashboardLineAreaPath(values: number[]) {
    const dots = this.dashboardLineDots(values);
    if (!dots.length) {
      return '';
    }
    const chartBottom = 192;
    const first = dots[0];
    const lines = dots.map((point) => `L ${point.x} ${point.y}`).join(' ');
    return `M ${first.x} ${chartBottom} ${lines} L ${dots[dots.length - 1].x} ${chartBottom} Z`;
  }

  dashboardLineYByValue(value: number) {
    const chartTop = 18;
    const chartBottom = 192;
    const maxValue = this.dashboardLineMaxValue || 1;
    const y = chartBottom - (Number(value || 0) / maxValue) * (chartBottom - chartTop);
    return Number(y.toFixed(2));
  }

  dashboardLinePointEnter(
    event: MouseEvent,
    point: { label: string; value: number },
    series: 'admissions' | 'trend'
  ) {
    this.updateDashboardLineTooltip(event, point, series);
  }

  dashboardLinePointMove(
    event: MouseEvent,
    point: { label: string; value: number },
    series: 'admissions' | 'trend'
  ) {
    this.updateDashboardLineTooltip(event, point, series);
  }

  dashboardLinePointLeave() {
    this.dashboardLineTooltip.visible = false;
  }

  get dashboardLineYAxisTicks() {
    const max = Math.max(1, Math.ceil(this.dashboardLineMaxValue));
    const divisions = 4;
    const step = max / divisions;
    const ticks: Array<{ label: string; value: number; y: number }> = [];
    for (let i = 0; i <= divisions; i += 1) {
      const value = Number((max - i * step).toFixed(1));
      const pretty = Number.isInteger(value) ? `${value}` : value.toLocaleString('pt-BR', {
        minimumFractionDigits: 1,
        maximumFractionDigits: 1
      });
      ticks.push({
        label: pretty,
        value,
        y: this.dashboardLineYByValue(value)
      });
    }
    return ticks;
  }

  dashboardLineDots(values: number[]) {
    const points: Array<{ x: number; y: number; value: number; label: string }> = [];
    if (!values.length) {
      return points;
    }
    const widthStart = 36;
    const widthEnd = 616;
    const chartTop = 18;
    const chartBottom = 192;
    const maxValue = this.dashboardLineMaxValue;
    const step = values.length > 1 ? (widthEnd - widthStart) / (values.length - 1) : 0;
    values.forEach((value, index) => {
      const x = widthStart + step * index;
      const y = chartBottom - (Number(value || 0) / maxValue) * (chartBottom - chartTop);
      points.push({
        x: Number(x.toFixed(2)),
        y: Number(y.toFixed(2)),
        value: Number(value || 0),
        label: this.admissionsByMonth[index]?.label || ''
      });
    });
    return points;
  }

  private updateDashboardLineTooltip(
    event: MouseEvent,
    point: { label: string; value: number },
    series: 'admissions' | 'trend'
  ) {
    const target = event.currentTarget as SVGCircleElement | null;
    const container = target?.closest('.dashboard-v2-line-chart') as HTMLElement | null;
    if (!container) {
      this.dashboardLineTooltip.visible = false;
      return;
    }
    const rect = container.getBoundingClientRect();
    let x = event.clientX - rect.left + 14;
    let y = event.clientY - rect.top - 12;
    x = Math.max(90, Math.min(x, rect.width - 20));
    y = Math.max(26, Math.min(y, rect.height - 20));
    const isAdmissions = series === 'admissions';
    this.dashboardLineTooltip = {
      visible: true,
      x,
      y,
      title: `${isAdmissions ? 'Admissões' : 'Tendência'} · ${point.label}`,
      value: isAdmissions
        ? `${point.value} contratação(ões)`
        : `${point.value.toLocaleString('pt-BR', { minimumFractionDigits: 1, maximumFractionDigits: 1 })} (média móvel)`
    };
  }


  get pagedEmployees() {
    const start = (this.currentPage - 1) * this.pageSize;
    return this.filteredEmployees.slice(start, start + this.pageSize);
  }

  get totalPages() {
    return Math.max(1, Math.ceil(this.filteredEmployees.length / this.pageSize));
  }

  get pagedHistory() {
    const start = (this.historyPage - 1) * this.historyPageSize;
    return this.history.slice(start, start + this.historyPageSize);
  }

  get historyTotalPages() {
    return Math.max(1, Math.ceil(this.history.length / this.historyPageSize));
  }

  goToPage(page: number) {
    if (page < 1 || page > this.totalPages) {
      return;
    }
    this.currentPage = page;
  }

  goToHistoryPage(page: number) {
    if (page < 1 || page > this.historyTotalPages) {
      return;
    }
    this.historyPage = page;
  }

  setActiveTab(tab: TabKey) {
    if (!this.isTabAllowed(tab)) {
      return;
    }
    this.activeTab = tab;
    if (tab === 'dashboard') {
      this.loadMoodThermometer(this.moodPeriodMonths);
      if (this.showDashboardFeedback) {
        this.loadFeedbackDashboard();
      }
    }
    if (tab === 'business') {
      this.loadBusinessActivities();
    }
    if (tab === 'forecast') {
      this.loadForecasts();
      this.loadBusinessActivities();
    }
    if (tab === 'closing') {
      this.loadClosings();
    }
    if (tab === 'reports') {
      this.clearReportFeedback();
    }
    try {
      localStorage.setItem(this.tabStorageKey, tab);
    } catch (_error) {
      // Ignore storage errors on restricted environments.
    }
  }

  get visibleTabs() {
    return this.allTabs
      .filter((tab) => this.allowedTabs.includes(tab))
      .filter((tab) => (tab !== 'imports' || this.isAdmin));
  }

  get showDashboardCollaborator() {
    if (!this.dashboardFilterTouched) {
      return true;
    }
    return this.dashboardFilter === 'ALL' || this.dashboardFilter === 'COLLABORATOR';
  }

  get showDashboardThermometer() {
    if (!this.dashboardFilterTouched) {
      return false;
    }
    return this.dashboardFilter === 'ALL' || this.dashboardFilter === 'THERMOMETER';
  }

  get showDashboardFeedback() {
    if (!this.dashboardFilterTouched) {
      return false;
    }
    return this.dashboardFilter === 'ALL' || this.dashboardFilter === 'FEEDBACK';
  }

  get feedbackDashboardSummary() {
    return this.feedbackDashboard?.summary || {
      total: 0,
      draft: 0,
      scheduled: 0,
      applied: 0,
      unique_employees: 0,
      applied_rate: 0
    };
  }

  get selectedDashboardFeedbackTemplateLabel() {
    if (!this.dashboardFeedbackTemplateId) {
      return 'Todos os feedbacks';
    }
    const selected = this.feedbackTemplates.find((item) => String(item.id) === String(this.dashboardFeedbackTemplateId));
    return selected?.title || 'Feedback selecionado';
  }

  private ensureActiveTabAllowed() {
    if (this.isTabAllowed(this.activeTab)) {
      return;
    }
    const fallbackTab = this.visibleTabs[0];
    if (fallbackTab) {
      this.setActiveTab(fallbackTab);
      return;
    }
    this.activeTab = 'dashboard';
  }

  private isTabAllowed(tab: TabKey) {
    if (!this.allowedTabs.includes(tab)) {
      return false;
    }
    if (tab === 'imports' && !this.isAdmin) {
      return false;
    }
    return true;
  }

  openEmployeeModal(employee?: ControlDpEmployee) {
    this.employeeTab = 'personal';
    if (employee) {
      this.selectedEmployee = employee;
      this.employeeForm = {
        ...this.defaultEmployeeForm(),
        ...employee,
        birth_date: this.toInputDate(employee.birth_date),
        hired_at: this.toInputDate(employee.hired_at),
        experience_45_days: this.toInputDate(employee.experience_45_days),
        experience_90_days: this.toInputDate(employee.experience_90_days)
      };
      this.loadEmployeeExtras(employee.id);
      if (this.employeeForm.role_id && this.isSalaryEmpty(this.employeeForm.salary)) {
        this.onRoleChange();
      }
    } else {
      this.selectedEmployee = null;
      this.employeeForm = this.defaultEmployeeForm();
      this.attachments = [];
      this.occurrences = [];
      this.history = [];
      this.extraForm = this.defaultExtraForm();
    }
    this.showEmployeeModal = true;
  }

  onRoleChange() {
    if (!this.employeeForm.role_id) {
      return;
    }
    const role = this.roles.find((item) => String(item.id) === String(this.employeeForm.role_id));
    if (!role) {
      return;
    }
    if (role.base_salary !== null && role.base_salary !== undefined) {
      this.employeeForm.salary = String(role.base_salary);
    }
  }

  openOccurrenceModal(employee: ControlDpEmployee) {
    this.selectedEmployee = employee;
    this.occurrenceForm = this.defaultOccurrenceForm();
    this.clearOccurrenceAttachment();
    this.showOccurrenceModal = true;
  }

  openHistoryModal(employee: ControlDpEmployee) {
    this.selectedEmployee = employee;
    this.historyPage = 1;
    this.loadHistory(employee.id);
    this.showHistoryModal = true;
  }

  openEmployeeFeedbackModal(employee: ControlDpEmployee) {
    if (!employee?.id) {
      return;
    }
    this.employeeFeedbackTarget = employee;
    this.employeeFeedbackTab = 'REGISTRO';
    this.employeeFeedbackError = '';
    this.employeeFeedbackQuestionsLoading = false;
    this.employeeFeedbackSaving = false;
    this.employeeFeedbackForm = this.defaultEmployeeFeedbackForm();
    this.employeeFeedbackQuestions = [];
    this.employeeFeedbackAnswers = {};
    this.employeeFeedbackRecords = [];
    this.showEmployeeFeedbackModal = true;
    this.loadFeedbackTemplates();
    this.loadEmployeeFeedbackRecords(employee.id);
  }

  closeEmployeeFeedbackModal() {
    this.showEmployeeFeedbackModal = false;
    this.employeeFeedbackTarget = null;
    this.employeeFeedbackTab = 'REGISTRO';
    this.employeeFeedbackLoading = false;
    this.employeeFeedbackQuestionsLoading = false;
    this.employeeFeedbackSaving = false;
    this.employeeFeedbackError = '';
    this.employeeFeedbackQuestions = [];
    this.employeeFeedbackAnswers = {};
    this.employeeFeedbackRecords = [];
    this.employeeFeedbackForm = this.defaultEmployeeFeedbackForm();
  }

  onEmployeeFeedbackTabClick(event: MouseEvent, tab: EmployeeFeedbackTab | string) {
    event.preventDefault();
    event.stopPropagation();
    this.setEmployeeFeedbackTab(tab);
  }

  setEmployeeFeedbackTab(tab: EmployeeFeedbackTab | string) {
    const normalized = String(tab || '').trim().toUpperCase();
    const allowedTabs: EmployeeFeedbackTab[] = ['REGISTRO', 'DRAFT', 'SCHEDULED', 'APPLIED'];
    const nextTab = (allowedTabs.includes(normalized as EmployeeFeedbackTab)
      ? (normalized as EmployeeFeedbackTab)
      : 'REGISTRO');
    if (this.employeeFeedbackTab === nextTab) {
      return;
    }
    this.employeeFeedbackTab = nextTab;
    this.employeeFeedbackError = '';
    if (this.employeeFeedbackTab !== 'REGISTRO' && this.employeeFeedbackTarget?.id && !this.employeeFeedbackLoading) {
      this.loadEmployeeFeedbackRecords(this.employeeFeedbackTarget.id);
    }
  }

  get filteredEmployeeFeedbackRecords() {
    if (this.employeeFeedbackTab === 'REGISTRO') {
      return [];
    }
    return this.employeeFeedbackRecords.filter((item) => String(item.status || '').toUpperCase() === this.employeeFeedbackTab);
  }

  get availableEmployeeFeedbackTemplates() {
    if (!this.employeeFeedbackTarget) {
      return this.feedbackTemplates;
    }
    const employeeSectorId = Number(this.employeeFeedbackTarget.sector_id || 0);
    const employeeDepartmentId = Number(this.employeeFeedbackTarget.department_id || 0);
    return this.feedbackTemplates.filter((template) => {
      const sectorMatches = !template.sector_id || Number(template.sector_id) === employeeSectorId;
      const departmentMatches = !template.department_id || Number(template.department_id) === employeeDepartmentId;
      return sectorMatches && departmentMatches;
    });
  }

  loadEmployeeFeedbackRecords(employeeId?: number) {
    const targetEmployeeId = Number(employeeId || this.employeeFeedbackTarget?.id || 0);
    if (!this.tenantId || !targetEmployeeId) {
      this.employeeFeedbackRecords = [];
      return;
    }
    this.employeeFeedbackLoading = true;
    this.employeeFeedbackError = '';
    this.service.listEmployeeFeedbacks(this.tenantId, targetEmployeeId).subscribe({
      next: (rows) => {
        this.employeeFeedbackRecords = rows || [];
        this.employeeFeedbackLoading = false;
      },
      error: () => {
        this.employeeFeedbackLoading = false;
        this.employeeFeedbackRecords = [];
        this.employeeFeedbackError = 'Não foi possível carregar os registros de feedback.';
      }
    });
  }

  editEmployeeFeedbackRecord(record: ControlDpEmployeeFeedback) {
    const parsedAnswers = this.parseEmployeeFeedbackAnswers(record.answers_json || null);
    this.employeeFeedbackForm = {
      feedback_id: Number(record.id || 0) || null,
      feedback_template_id: record.feedback_template_id ? String(record.feedback_template_id) : '',
      notes: String(record.notes || ''),
      collaborator_notes: String(record.collaborator_notes || ''),
      scheduled_for: this.toDateTimeLocalInput(record.scheduled_for || null)
    };
    this.employeeFeedbackTab = 'REGISTRO';
    this.employeeFeedbackError = '';
    this.loadEmployeeFeedbackQuestionsByTemplateId(Number(record.feedback_template_id || 0), parsedAnswers);
  }

  onEmployeeFeedbackTemplateChange(templateIdValue: string | number) {
    const templateId = Number(templateIdValue || 0);
    this.employeeFeedbackQuestions = [];
    this.employeeFeedbackAnswers = {};
    this.employeeFeedbackForm.feedback_id = null;
    if (!templateId) {
      this.employeeFeedbackQuestionsLoading = false;
      return;
    }
    this.loadEmployeeFeedbackQuestionsByTemplateId(templateId);
  }

  saveEmployeeFeedback(status: EmployeeFeedbackStatus) {
    if (!this.tenantId || !this.employeeFeedbackTarget?.id || this.employeeFeedbackSaving) {
      return;
    }
    const templateId = Number(this.employeeFeedbackForm.feedback_template_id);
    if (!templateId) {
      this.employeeFeedbackError = 'Selecione um template de feedback.';
      return;
    }
    if (status === 'SCHEDULED' && !String(this.employeeFeedbackForm.scheduled_for || '').trim()) {
      this.employeeFeedbackError = 'Informe a data/hora para agendar.';
      return;
    }
    if (!this.employeeFeedbackQuestions.length) {
      this.employeeFeedbackError = 'Esse template não possui perguntas disponíveis para resposta.';
      return;
    }

    const requiredError = this.validateEmployeeFeedbackRequiredAnswers();
    if (requiredError) {
      this.employeeFeedbackError = requiredError;
      return;
    }

    const answersPayload = this.serializeEmployeeFeedbackAnswers();

    this.employeeFeedbackSaving = true;
    this.employeeFeedbackError = '';
    this.service
      .saveEmployeeFeedback(this.employeeFeedbackTarget.id, {
        tenant_id: this.tenantId,
        feedback_id: this.employeeFeedbackForm.feedback_id,
        feedback_template_id: templateId,
        status,
        scheduled_for: status === 'SCHEDULED' ? this.employeeFeedbackForm.scheduled_for : null,
        notes: this.employeeFeedbackForm.notes || null,
        collaborator_notes: this.employeeFeedbackForm.collaborator_notes || null,
        answers_json: answersPayload,
        created_by: this.userId || null,
        updated_by: this.userId || null
      })
      .subscribe({
        next: () => {
          this.employeeFeedbackSaving = false;
          this.employeeFeedbackTab = status;
          this.employeeFeedbackForm = this.defaultEmployeeFeedbackForm();
          this.employeeFeedbackQuestions = [];
          this.employeeFeedbackAnswers = {};
          this.loadEmployeeFeedbackRecords(this.employeeFeedbackTarget?.id);
        },
        error: (error) => {
          this.employeeFeedbackSaving = false;
          this.employeeFeedbackError = error?.error?.error || 'Não foi possível salvar o feedback.';
        }
      });
  }

  async loadEmployeeFeedbackQuestionsByTemplateId(templateId: number, prefilledAnswers?: Record<string, any>) {
    if (!this.tenantId || !templateId) {
      this.employeeFeedbackQuestions = [];
      this.employeeFeedbackAnswers = {};
      this.employeeFeedbackQuestionsLoading = false;
      return;
    }
    const template = this.feedbackTemplates.find((item) => Number(item.id) === Number(templateId));
    const fallbackRecord = this.employeeFeedbackRecords.find((item) => Number(item.feedback_template_id) === Number(templateId));
    const surveyId = Number(template?.survey_id || fallbackRecord?.survey_id || 0);
    if (!surveyId) {
      this.employeeFeedbackQuestions = [];
      this.employeeFeedbackAnswers = {};
      this.employeeFeedbackQuestionsLoading = false;
      this.employeeFeedbackError = 'Template selecionado sem questionário vinculado.';
      return;
    }
    const requestId = ++this.employeeFeedbackQuestionRequestId;
    this.employeeFeedbackQuestionsLoading = true;
    this.employeeFeedbackError = '';

    try {
      const detail = await firstValueFrom(this.controlquestion.getSurvey(this.tenantId, surveyId));
      if (requestId !== this.employeeFeedbackQuestionRequestId) {
        return;
      }
      const questions = Array.isArray(detail?.questions) ? detail.questions : [];
      const options = Array.isArray(detail?.options) ? detail.options : [];

      const mappedQuestions: EmployeeFeedbackQuestion[] = questions
        .slice()
        .sort(
          (a: ControlQuestion, b: ControlQuestion) =>
            (Number(a.order_index || 0) - Number(b.order_index || 0)) || (Number(a.id || 0) - Number(b.id || 0))
        )
        .map((question: ControlQuestion) => {
          const questionOptions = options
            .filter((option: ControlQuestionOption) => Number(option.question_id) === Number(question.id))
            .sort(
              (a: ControlQuestionOption, b: ControlQuestionOption) =>
                (Number(a.order_index || 0) - Number(b.order_index || 0)) || (Number(a.id || 0) - Number(b.id || 0))
            )
            .map((option: ControlQuestionOption) => ({
              id: option.id ? Number(option.id) : null,
              label: String(option.label || ''),
              order_index: Number(option.order_index || 0)
            }));

          return {
            id: Number(question.id),
            title: String(question.title || ''),
            description: String(question.description || ''),
            question_type: String(question.question_type || 'TEXT').toUpperCase(),
            required: Number(question.required) === 1,
            order_index: Number(question.order_index || 0),
            options: questionOptions
          };
        });

      this.employeeFeedbackQuestions = mappedQuestions;
      this.employeeFeedbackAnswers = this.buildEmployeeFeedbackAnswersModel(mappedQuestions, prefilledAnswers);
      if (!mappedQuestions.length) {
        this.employeeFeedbackError = 'Esse template não possui perguntas disponíveis.';
      }
    } catch (_error) {
      if (requestId === this.employeeFeedbackQuestionRequestId) {
        this.employeeFeedbackQuestions = [];
        this.employeeFeedbackAnswers = {};
        this.employeeFeedbackError = 'Não foi possível carregar as perguntas do template.';
      }
    } finally {
      if (requestId === this.employeeFeedbackQuestionRequestId) {
        this.employeeFeedbackQuestionsLoading = false;
      }
    }
  }

  employeeFeedbackQuestionAnswer(questionId: number) {
    return this.employeeFeedbackAnswers[String(questionId)];
  }

  setEmployeeFeedbackQuestionAnswer(questionId: number, value: any) {
    this.employeeFeedbackAnswers[String(questionId)] = value;
  }

  employeeFeedbackOptionValue(option: EmployeeFeedbackQuestionOption) {
    const optionId = Number(option.id || 0);
    if (optionId) {
      return String(optionId);
    }
    return String(option.label || '').trim();
  }

  isEmployeeFeedbackOptionChecked(questionId: number, optionValue: string) {
    const current = this.employeeFeedbackQuestionAnswer(questionId);
    if (!Array.isArray(current)) {
      return false;
    }
    return current.map((item) => String(item)).includes(String(optionValue));
  }

  toggleEmployeeFeedbackOption(questionId: number, optionValue: string, checked: boolean) {
    const current = this.employeeFeedbackQuestionAnswer(questionId);
    const values = Array.isArray(current) ? current.map((item) => String(item)) : [];
    const normalized = String(optionValue);
    if (checked) {
      if (!values.includes(normalized)) {
        values.push(normalized);
      }
    } else {
      const nextValues = values.filter((item) => item !== normalized);
      this.employeeFeedbackAnswers[String(questionId)] = nextValues;
      return;
    }
    this.employeeFeedbackAnswers[String(questionId)] = values;
  }

  isEmployeeFeedbackChoiceQuestion(question: EmployeeFeedbackQuestion) {
    const type = String(question.question_type || '').toUpperCase();
    return ['SINGLE_CHOICE', 'DROPDOWN'].includes(type);
  }

  isEmployeeFeedbackMultipleQuestion(question: EmployeeFeedbackQuestion) {
    const type = String(question.question_type || '').toUpperCase();
    return type === 'MULTIPLE_CHOICE';
  }

  isEmployeeFeedbackNumberQuestion(question: EmployeeFeedbackQuestion) {
    return String(question.question_type || '').toUpperCase() === 'NUMBER';
  }

  isEmployeeFeedbackDateQuestion(question: EmployeeFeedbackQuestion) {
    return String(question.question_type || '').toUpperCase() === 'DATE';
  }

  isEmployeeFeedbackEmailQuestion(question: EmployeeFeedbackQuestion) {
    return String(question.question_type || '').toUpperCase() === 'EMAIL';
  }

  employeeFeedbackStatusLabel(value?: string | null) {
    const normalized = String(value || '').toUpperCase();
    if (normalized === 'DRAFT') return 'Rascunho';
    if (normalized === 'SCHEDULED') return 'Agendado';
    if (normalized === 'APPLIED') return 'Aplicado';
    if (normalized === 'REGISTRO') return 'Registro';
    return 'Registro';
  }

  loadFeedbackTemplates() {
    if (!this.tenantId) {
      this.feedbackTemplates = [];
      return;
    }
    this.feedbackTemplatesLoading = true;
    this.service.listFeedbackTemplates(this.tenantId).subscribe({
      next: (rows) => {
        this.feedbackTemplates = rows || [];
        this.feedbackTemplatesLoading = false;
      },
      error: () => {
        this.feedbackTemplates = [];
        this.feedbackTemplatesLoading = false;
      }
    });
  }

  openRoleModal() {
    this.roleForm = { name: '', base_salary: '' };
    this.editingRole = null;
    this.showRoleModal = true;
  }

  editRole(role: ControlDpRole) {
    this.editingRole = role;
    this.roleForm = {
      name: role.name,
      base_salary: role.base_salary !== null && role.base_salary !== undefined ? String(role.base_salary) : ''
    };
    this.showRoleModal = true;
  }

  openImportModal(importType: string) {
    this.importType = importType;
    this.importFileName = '';
    this.importPreviewRows = [];
    this.importHeaders = [];
    this.importFeedback = '';
    this.importErrorRows = [];
    this.showImportModal = true;
  }

  openFilterModal() {
    this.loadFeedbackTemplates();
    this.showFilterModal = true;
  }

  closeFilterModal() {
    this.showFilterModal = false;
  }

  openBusinessModal() {
    this.businessForm = this.defaultBusinessForm();
    this.businessProcessFile = null;
    this.businessMeoFile = null;
    this.businessAttachments = [];
    this.businessOtherProcessAttachments = [];
    this.businessManualAttachments = [];
    this.businessExistingProcessAttachment = null;
    this.businessExistingMeoAttachment = null;
    this.businessExistingOtherAttachments = [];
    this.businessExistingManualAttachments = [];
    this.businessClientSystems = [];
    this.businessAcertSystems = [];
    this.businessSupplierSystems = [];
    this.businessClientInput = '';
    this.businessAcertInput = '';
    this.businessSupplierInput = '';
    this.showBusinessDetails = false;
    this.showBusinessTimes = false;
    this.showBusinessProcesses = false;
    this.showBusinessSystems = false;
    this.showBusinessManuals = false;
    this.editingBusinessId = null;
    this.showBusinessModal = true;
  }

  closeBusinessModal() {
    this.showBusinessModal = false;
  }

  openBusinessEdit(activity: any) {
    this.editingBusinessId = activity.id;
    this.editingBusinessSnapshot = { ...activity };
    this.businessForm = {
      ...this.defaultBusinessForm(),
      ...activity,
      tma: this.normalizeTimeForInput(activity?.tma),
      tme: this.normalizeTimeForInput(activity?.tme),
      tmo: this.normalizeTimeForInput(activity?.tmo),
      tmc: this.normalizeTimeForInput(activity?.tmc)
    };
    this.businessClientSystems = [...(activity.client_systems || [])];
    this.businessAcertSystems = [...(activity.acert_systems || [])];
    this.businessSupplierSystems = [...(activity.supplier_systems || [])];
    this.businessProcessFile = null;
    this.businessMeoFile = null;
    this.businessOtherProcessAttachments = [];
    this.businessManualAttachments = [];
    this.businessExistingProcessAttachment = activity.process_attachment || null;
    this.businessExistingMeoAttachment = activity.meo_attachment || null;
    this.businessExistingOtherAttachments = [...(activity.other_attachments || [])];
    this.businessExistingManualAttachments = [...(activity.manual_attachments || [])];
    this.businessClientInput = '';
    this.businessAcertInput = '';
    this.businessSupplierInput = '';
    this.showBusinessDetails = true;
    this.showBusinessTimes = true;
    this.showBusinessProcesses = true;
    this.showBusinessSystems = true;
    this.showBusinessManuals = true;
    this.showBusinessModal = true;
  }

  openBusinessView(activity: any) {
    this.selectedBusinessView = activity;
    this.showBusinessViewModal = true;
  }

  closeBusinessViewModal() {
    this.showBusinessViewModal = false;
    this.selectedBusinessView = null;
  }

  openBusinessHistory(activity: any) {
    this.selectedBusinessHistory = this.businessHistory[activity.id] || [];
    this.showBusinessHistoryModal = true;
  }

  closeBusinessHistoryModal() {
    this.showBusinessHistoryModal = false;
    this.selectedBusinessHistory = [];
  }

  onBusinessFileSelected(type: 'process' | 'meo' | 'others' | 'manuals', event: Event) {
    const input = event.target as HTMLInputElement;
    if (!input.files?.length) {
      return;
    }
    if (type === 'others') {
      this.businessOtherProcessAttachments = Array.from(input.files);
      return;
    }
    if (type === 'manuals') {
      this.businessManualAttachments = Array.from(input.files);
      return;
    }
    const file = input.files[0];
    if (type === 'process') {
      this.businessProcessFile = file;
    } else {
      this.businessMeoFile = file;
    }
  }

  submitBusinessActivity() {
    if (!this.tenantId) {
      return;
    }
    const formData = this.buildBusinessFormData();
    if (this.userId) {
      formData.append(this.editingBusinessId ? 'updated_by' : 'created_by', String(this.userId));
    }
    const request = this.editingBusinessId
      ? this.controlopService.updateActivity(this.editingBusinessId, formData)
      : this.controlopService.createActivity(formData);

    request.subscribe({
      next: (activity) => {
        if (activity?.id) {
          const action = this.editingBusinessId ? 'update' : 'create';
          const before = this.editingBusinessId ? this.editingBusinessSnapshot : null;
          this.pushBusinessHistory(activity.id, action, before, activity);
        }
        this.editingBusinessSnapshot = null;
        this.editingBusinessId = null;
        this.closeBusinessModal();
        this.loadBusinessActivities();
      },
      error: (error) => {
        console.error('Erro ao salvar atividade:', error);
      }
    });
  }

  fillBusinessTestData() {
    const scopedDepartments = this.scopedDepartmentsForControlopForms;
    this.businessForm = {
      ...this.businessForm,
      title: 'Atendimento Operacional',
      business_model: 'Ticket/PA',
      journey: 'Inbound + Chat',
      department_id: scopedDepartments.length ? scopedDepartments[0].id : '',
      description: 'Atividade focada em suporte e resolucao de demandas operacionais.',
      sla: 'SLA de resposta em ate 5 minutos.',
      tma: '00:04',
      tme: '00:03',
      tmo: '00:06',
      tmc: '00:02',
      observations: 'Preenchido automaticamente para teste.'
    };
    this.businessClientSystems = ['CRM', 'Portal do Cliente'];
    this.businessAcertSystems = ['ControlOP', 'Base interna'];
    this.businessSupplierSystems = ['Fornecedor X'];
  }

  applyFilters() {
    this.currentPage = 1;
    this.showFilterModal = false;
    this.loadMoodThermometer(this.moodPeriodMonths);
    if (this.activeTab === 'dashboard' && this.showDashboardFeedback) {
      this.loadFeedbackDashboard();
    }
  }

  clearFilters() {
    this.filterSectorId = '';
    this.filterDepartmentId = '';
    this.dashboardFilter = 'ALL';
    this.dashboardFilterTouched = false;
    this.dashboardFeedbackTemplateId = '';
    this.currentPage = 1;
    this.loadMoodThermometer(this.moodPeriodMonths);
    if (this.activeTab === 'dashboard') {
      this.loadFeedbackDashboard();
    }
  }

  onDashboardFilterChange() {
    this.dashboardFilterTouched = true;
    if (this.dashboardFilter !== 'FEEDBACK') {
      this.dashboardFeedbackTemplateId = '';
    }
    if (this.activeTab === 'dashboard' && this.showDashboardFeedback) {
      this.loadFeedbackDashboard();
    }
  }

  onDashboardFeedbackTemplateChange() {
    if (this.activeTab === 'dashboard' && this.showDashboardFeedback) {
      this.loadFeedbackDashboard();
    }
  }

  loadFeedbackDashboard() {
    if (!this.tenantId) {
      this.feedbackDashboard = null;
      return;
    }
    const templateId = Number(this.dashboardFeedbackTemplateId || 0) || null;
    this.feedbackDashboardLoading = true;
    this.service.getFeedbackDashboard(this.tenantId, templateId, this.moodPeriodMonths).subscribe({
      next: (payload) => {
        this.feedbackDashboard = payload || null;
        this.feedbackDashboardLoading = false;
      },
      error: () => {
        this.feedbackDashboard = null;
        this.feedbackDashboardLoading = false;
      }
    });
  }

  closeModals() {
    this.showEmployeeModal = false;
    this.showOccurrenceModal = false;
    this.showHistoryModal = false;
    this.showEmployeeFeedbackModal = false;
    this.showRoleModal = false;
    this.editingRole = null;
    this.showImportModal = false;
    this.showBusinessModal = false;
    this.showBusinessHistoryModal = false;
    this.showBusinessViewModal = false;
    this.selectedBusinessView = null;
    this.showForecastModal = false;
    this.showForecastHistoryModal = false;
    this.forecastHistory = [];
    this.editingForecastId = null;
    this.employeeFeedbackTarget = null;
    this.employeeFeedbackTab = 'REGISTRO';
    this.employeeFeedbackLoading = false;
    this.employeeFeedbackQuestionsLoading = false;
    this.employeeFeedbackSaving = false;
    this.employeeFeedbackError = '';
    this.employeeFeedbackQuestions = [];
    this.employeeFeedbackAnswers = {};
    this.employeeFeedbackRecords = [];
    this.employeeFeedbackForm = this.defaultEmployeeFeedbackForm();
    this.clearOccurrenceAttachment();
  }

  loadEmployeeExtras(employeeId: number) {
    if (!this.tenantId) return;
    this.service.listOccurrences(this.tenantId, employeeId).subscribe({
      next: (rows) => (this.occurrences = rows || []),
      error: () => (this.occurrences = [])
    });
    this.service.listAttachments(this.tenantId, employeeId).subscribe({
      next: (rows) => (this.attachments = rows || []),
      error: () => (this.attachments = [])
    });
    this.loadHistory(employeeId);
    this.extraLoading = true;
    this.controlopService.getEmployeeExtras(this.tenantId, employeeId).subscribe({
      next: (payload) => {
        const normalized = this.normalizeExtraJson(payload?.extra_json);
        this.extraForm = {
          ...this.defaultExtraForm(),
          ...payload,
          extra_json: normalized
        };
        this.extraLoading = false;
      },
      error: () => {
        this.extraForm = this.defaultExtraForm();
        this.extraLoading = false;
      }
    });
  }

  private normalizeExtraJson(raw: any) {
    if (!raw) {
      return {};
    }
    if (typeof raw === 'object') {
      return raw;
    }
    try {
      return JSON.parse(String(raw));
    } catch (_error) {
      return {};
    }
  }

  loadHistory(employeeId: number) {
    if (!this.tenantId) return;
    this.service.listHistory(this.tenantId, employeeId).subscribe({
      next: (rows) => {
        this.history = rows || [];
        this.historyPage = 1;
      },
      error: () => (this.history = [])
    });
  }

  saveEmployee() {
    if (!this.tenantId || !this.userId) return;
    const payload = {
      tenant_id: this.tenantId,
      created_by: this.userId,
      updated_by: this.userId,
      status: this.employeeForm.status,
      full_name: this.employeeForm.full_name,
      employee_code: this.employeeForm.employee_code,
      cpf: this.employeeForm.cpf,
      rg: this.employeeForm.rg,
      birth_date: this.employeeForm.birth_date,
      birth_location: this.employeeForm.birth_location,
      race: this.employeeForm.race,
      gender: this.employeeForm.gender,
      marital_status: this.employeeForm.marital_status,
      mother_name: this.employeeForm.mother_name,
      father_name: this.employeeForm.father_name,
      education_level: this.employeeForm.education_level,
      personal_email: this.employeeForm.personal_email,
      corporate_email: this.employeeForm.corporate_email,
      phone: this.employeeForm.phone,
      cep: this.employeeForm.cep,
      street: this.employeeForm.street,
      number: this.employeeForm.number,
      complement: this.employeeForm.complement,
      neighborhood: this.employeeForm.neighborhood,
      city: this.employeeForm.city,
      state: this.employeeForm.state,
      emergency_contact_name: this.employeeForm.emergency_contact_name,
      emergency_contact_phone: this.employeeForm.emergency_contact_phone,
      hired_at: this.employeeForm.hired_at,
      esocial_code: this.employeeForm.esocial_code,
      ctps_number: this.employeeForm.ctps_number,
      pis: this.employeeForm.pis,
      pcd: this.employeeForm.pcd ? 1 : 0,
      cbo_code: this.employeeForm.cbo_code,
      bank: this.employeeForm.bank,
      bank_branch: this.employeeForm.bank_branch,
      bank_account: this.employeeForm.bank_account,
      bank_account_type: this.employeeForm.bank_account_type,
      contract_type: this.employeeForm.contract_type,
      salary: this.toNumberOrNull(this.employeeForm.salary),
      role_id: this.toNumberOrNull(this.employeeForm.role_id),
      sector_id: this.toNumberOrNull(this.employeeForm.sector_id),
      department_id: this.toNumberOrNull(this.employeeForm.department_id),
      location_id: this.toNumberOrNull(this.employeeForm.location_id),
      notes: this.employeeForm.notes
    };
    const request$ = this.selectedEmployee
      ? this.service.updateEmployee(this.selectedEmployee.id, payload)
      : this.service.createEmployee(payload);
    request$.subscribe({
      next: () => {
        this.loadEmployees();
        this.loadDashboard();
        this.closeModals();
      }
    });
  }

  saveEmployeeExtras() {
    if (!this.tenantId || !this.userId || !this.selectedEmployee) {
      return;
    }
    const payload = {
      tenant_id: this.tenantId,
      employee_id: this.selectedEmployee.id,
      extra_notes: this.extraForm.extra_notes || '',
      extra_json: {
        badge_code: this.extraForm.extra_json?.badge_code || '',
        access_profile: this.extraForm.extra_json?.access_profile || '',
        shift: this.extraForm.extra_json?.shift || '',
        uniform_size: this.extraForm.extra_json?.uniform_size || ''
      },
      created_by: this.userId
    };
    this.extraLoading = true;
    this.controlopService.saveEmployeeExtras(this.tenantId, this.selectedEmployee.id, payload).subscribe({
      next: () => {
        this.extraLoading = false;
        this.loadEmployeeExtras(this.selectedEmployee!.id);
      },
      error: () => {
        this.extraLoading = false;
      }
    });
  }

  saveOccurrence() {
    if (!this.tenantId || !this.userId || !this.selectedEmployee) return;
    if (this.occurrenceForm.type !== 'PROMOTION') {
      this.occurrenceForm.promoted_role_id = '';
      this.occurrenceForm.promoted_salary = '';
    }
    const payload = {
      ...this.occurrenceForm,
      type: this.occurrenceForm.type,
      tenant_id: this.tenantId,
      employee_id: this.selectedEmployee.id,
      created_by: this.userId
    };
    this.service.createOccurrence(payload).subscribe({
      next: (occurrence) => {
        if (this.occurrenceAttachmentFile) {
          this.service
            .uploadOccurrenceAttachment(this.tenantId!, occurrence.id, this.userId!, this.occurrenceAttachmentFile)
            .subscribe({
              next: () => this.finalizeOccurrenceSave(),
              error: () => this.finalizeOccurrenceSave()
            });
          return;
        }
        this.finalizeOccurrenceSave();
      }
    });
  }

  saveRole() {
    if (!this.tenantId || !this.roleForm.name.trim()) return;
    const payload = {
      tenant_id: this.tenantId,
      name: this.roleForm.name.trim(),
      base_salary: this.roleForm.base_salary ? Number(this.roleForm.base_salary) : null,
      created_by: this.userId
    };
    const request$ = this.editingRole
      ? this.service.updateRole(this.editingRole.id, payload)
      : this.service.createRole(payload);
    request$.subscribe({
      next: () => {
        this.loadRoles();
        this.closeModals();
      }
    });
  }

  onAttachmentSelected(event: Event) {
    if (!this.tenantId || !this.userId || !this.selectedEmployee) return;
    const input = event.target as HTMLInputElement;
    const file = input.files?.[0];
    if (!file) return;
    this.service.uploadAttachment(this.tenantId, this.selectedEmployee.id, this.userId, file).subscribe({
      next: () => this.loadEmployeeExtras(this.selectedEmployee!.id)
    });
  }

  formatAttachmentUrl(path?: string | null) {
    if (!path) return '#';
    if (path.startsWith('http')) return path;
    return `${this.apiBase}${path}`;
  }

  get forecastActivityOptions() {
    if (!this.businessActivities.length) {
      return [];
    }
    if (!this.hasControlopScope) {
      return this.businessActivities;
    }
    if (!this.allowedDepartmentIds.length) {
      return this.businessActivities;
    }
    return this.businessActivities.filter((activity) =>
      this.allowedDepartmentIds.includes(Number(activity.department_id))
    );
  }

  openForecastModal() {
    this.forecastForm = this.defaultForecastForm();
    this.forecastActivities = [this.defaultForecastActivity()];
    this.editingForecastId = null;
    this.forecastExpandedIndex = 0;
    this.forecastValidationError = '';
    this.clearForecastFeedback();
    if (!this.businessActivities.length) {
      this.loadBusinessActivities();
    }
    this.showForecastModal = true;
  }

  openForecastEdit(item: any) {
    this.editingForecastId = item.id;
    this.forecastForm = {
      title: item.title || ''
    };
    this.forecastValidationError = '';
    this.clearForecastFeedback();
    this.clearForecastPageFeedback();
    this.forecastActivities = (item.activities || []).map((activity: any) => ({
      activity_id: activity.activity_id ? String(activity.activity_id) : '',
      department_id: activity.department_id ? String(activity.department_id) : '',
      linked: !!activity.activity_id,
      received_date: this.toInputDate(activity.received_date),
      reference_month: this.toMonthInput(activity.reference_month),
      forecast_month: activity.forecast_month ?? '',
      forecast_week: activity.forecast_week ?? '',
      forecast_day: activity.forecast_day ?? '',
      forecast_hour: activity.forecast_hour ?? '',
      notes: activity.notes || '',
      attachments: activity.attachments || [],
      newAttachments: []
    }));
    if (!this.forecastActivities.length) {
      this.forecastActivities = [this.defaultForecastActivity()];
    }
    this.forecastExpandedIndex = 0;
    if (!this.businessActivities.length) {
      this.loadBusinessActivities();
    }
    this.showForecastModal = true;
  }

  closeForecastModal() {
    this.showForecastModal = false;
  }

  addForecastActivity() {
    const currentIndex = this.forecastExpandedIndex;
    if (currentIndex >= 0 && this.forecastActivities[currentIndex]) {
      const missing = this.getForecastActivityMissingFields(this.forecastActivities[currentIndex]);
      if (missing.length) {
        this.forecastValidationError = 'Preencha todos os campos obrigatorios.';
        this.setForecastFeedback('warning', 'Preencha todos os campos obrigatorios.');
        return;
      }
    }
    this.forecastActivities.push(this.defaultForecastActivity());
    this.forecastExpandedIndex = this.forecastActivities.length - 1;
    this.forecastValidationError = '';
    this.setForecastFeedback('success', 'Nova atividade inserida.');
  }

  removeForecastActivity(index: number) {
    this.forecastActivities.splice(index, 1);
    if (!this.forecastActivities.length) {
      this.forecastActivities = [this.defaultForecastActivity()];
      this.forecastExpandedIndex = 0;
      return;
    }
    if (this.forecastExpandedIndex === index) {
      this.forecastExpandedIndex = 0;
    } else if (this.forecastExpandedIndex > index) {
      this.forecastExpandedIndex -= 1;
    }
  }

  toggleForecastActivity(index: number) {
    if (this.forecastExpandedIndex === index) {
      this.forecastExpandedIndex = -1;
      return;
    }
    if (this.forecastExpandedIndex !== -1) {
      this.setForecastFeedback('warning', 'Ja existe uma atividade aberta.');
      return;
    }
    this.forecastExpandedIndex = index;
  }

  onForecastActivityLinkToggle(index: number) {
    const activity = this.forecastActivities[index];
    if (!activity) {
      return;
    }
    if (!activity.linked) {
      activity.activity_id = '';
      this.forecastValidationError = '';
      this.clearForecastFeedback();
      return;
    }
    activity.department_id = '';
    this.forecastValidationError = '';
    this.clearForecastFeedback();
  }

  isForecastActivityExpanded(index: number) {
    return this.forecastExpandedIndex === index;
  }

  onForecastAttachmentSelected(index: number, event: Event) {
    const input = event.target as HTMLInputElement;
    if (!input.files?.length) {
      return;
    }
    const files = Array.from(input.files);
    const target = this.forecastActivities[index];
    if (!target) {
      return;
    }
    target.newAttachments = [...(target.newAttachments || []), ...files];
  }

  submitForecast() {
    if (!this.tenantId || !this.userId) {
      return;
    }
    this.forecastValidationError = '';
    const missingIndex = this.forecastActivities.findIndex((activity) =>
      this.getForecastActivityMissingFields(activity).length
    );
    if (missingIndex !== -1) {
      this.forecastValidationError = `Preencha todos os campos obrigatorios da atividade ${missingIndex + 1}.`;
      this.forecastExpandedIndex = missingIndex;
      this.setForecastFeedback('warning', 'Preencha todos os campos obrigatorios.');
      return;
    }
    const formData = this.buildForecastFormData();
    if (this.userId) {
      formData.append(this.editingForecastId ? 'updated_by' : 'created_by', String(this.userId));
    }
    const request = this.editingForecastId
      ? this.controlopService.updateForecast(this.editingForecastId, formData)
      : this.controlopService.createForecast(formData);

    request.subscribe({
      next: () => {
        this.closeForecastModal();
        this.loadForecasts();
        const message = this.editingForecastId ? 'Forecast atualizado com sucesso.' : 'Forecast cadastrado com sucesso.';
        this.setForecastPageFeedback('success', message);
      },
      error: (error) => {
        console.error('Erro ao salvar forecast:', error);
      }
    });
  }

  openForecastHistory(item: any) {
    if (!this.tenantId) {
      return;
    }
    this.controlopService.listForecastHistory(this.tenantId, item.id).subscribe({
      next: (rows) => {
        this.forecastHistory = rows || [];
        this.showForecastHistoryModal = true;
      },
      error: () => {
        this.forecastHistory = [];
        this.showForecastHistoryModal = true;
      }
    });
  }

  closeForecastHistoryModal() {
    this.showForecastHistoryModal = false;
    this.forecastHistory = [];
  }

  openClosingModal(entry?: any) {
    this.closingValidationError = '';
    if (entry) {
      this.editingClosingId = entry.id;
      this.closingForm = {
        department_id: entry.department_id ? String(entry.department_id) : '',
        closing_date: this.toInputDate(entry.closing_date),
        goal: entry.goal ?? '',
        achieved: entry.achieved ?? '',
        delivery_percent: entry.delivery_percent ?? '',
        notes: entry.notes || ''
      };
    } else {
      this.editingClosingId = null;
      this.closingForm = this.defaultClosingForm();
    }
    this.updateClosingDeliveryPercent();
    this.showClosingModal = true;
  }

  closeClosingModal() {
    this.showClosingModal = false;
  }

  submitClosing() {
    if (!this.tenantId || !this.userId) {
      return;
    }
    this.closingValidationError = '';
    this.updateClosingDeliveryPercent();
    if (!this.closingForm.department_id || !this.closingForm.closing_date) {
      this.closingValidationError = 'Departamento e data são obrigatórios.';
      return;
    }
    if (
      this.closingForm.goal === '' ||
      this.closingForm.achieved === '' ||
      this.closingForm.delivery_percent === ''
    ) {
      this.closingValidationError = 'Preencha meta, realizado e % entrega.';
      return;
    }
    const payload = {
      tenant_id: this.tenantId,
      department_id: this.closingForm.department_id,
      closing_date: this.closingForm.closing_date,
      goal: this.toNumberOrNull(this.closingForm.goal),
      achieved: this.toNumberOrNull(this.closingForm.achieved),
      delivery_percent: this.toNumberOrNull(this.closingForm.delivery_percent),
      notes: this.closingForm.notes || null,
      created_by: this.userId,
      updated_by: this.userId
    };
    const request = this.editingClosingId
      ? this.controlopService.updateClosing(this.editingClosingId, payload)
      : this.controlopService.createClosing(payload);
    request.subscribe({
      next: () => {
        this.closeClosingModal();
        this.loadClosings();
      },
      error: () => {
        this.closingValidationError = 'Erro ao salvar fechamento.';
      }
    });
  }

  generateBusinessReport() {
    if (!this.reportBusinessDepartmentId) {
      this.setReportFeedback('warning', 'Selecione um departamento para gerar o relatorio.');
      return;
    }
    const departmentId = Number(this.reportBusinessDepartmentId);
    this.setReportFeedback('', '');
    this.ensureOccurrencesLoaded().then((occurrences) => {
      this.buildBusinessReportPdf(departmentId, occurrences);
    });
  }

  generateClosingReport() {
    this.setReportFeedback('', '');
    const departmentId =
      this.reportClosingDepartmentId && this.reportClosingDepartmentId !== 'all'
        ? Number(this.reportClosingDepartmentId)
        : null;
    this.buildClosingReportPdf(departmentId);
  }

  formatTimeLabel(value: any) {
    const normalized = this.normalizeTimeForInput(value);
    return normalized || '—';
  }

  getDepartmentLabel(departmentId: any) {
    if (!departmentId) {
      return '—';
    }
    const dept = this.departments.find((item) => String(item.id) === String(departmentId));
    if (!dept) {
      return `Departamento ${departmentId}`;
    }
    const sectorName = dept.sector_name || this.getSectorName(dept.sector_id);
    return sectorName ? `${sectorName} / ${dept.name}` : dept.name;
  }

  private getSectorName(sectorId: any) {
    if (!sectorId) {
      return '';
    }
    const sector = this.sectors.find((item) => String(item.id) === String(sectorId));
    return sector?.name || '';
  }

  private normalizeTimeForInput(value: any) {
    if (value === null || value === undefined || value === '') {
      return '';
    }
    if (typeof value === 'number' && Number.isFinite(value)) {
      return this.minutesToTime(value);
    }
    const raw = String(value).trim();
    if (!raw) {
      return '';
    }
    if (raw.includes(':')) {
      const parts = raw.split(':');
      const hours = String(parts[0] || '0').padStart(2, '0');
      const minutes = String(parts[1] || '0').padStart(2, '0');
      return `${hours}:${minutes}`;
    }
    const normalized = raw.replace(',', '.');
    const minutes = Number(normalized);
    if (Number.isFinite(minutes)) {
      return this.minutesToTime(minutes);
    }
    return raw;
  }

  private minutesToTime(value: number) {
    const totalMinutes = Math.max(0, Math.round(value));
    const hours = Math.floor(totalMinutes / 60);
    const minutes = totalMinutes % 60;
    return `${String(hours).padStart(2, '0')}:${String(minutes).padStart(2, '0')}`;
  }

  private buildForecastFormData() {
    const formData = new FormData();
    formData.append('tenant_id', String(this.tenantId));
    formData.append('title', this.forecastForm.title || '');
    const hasActivities = this.forecastActivities.some((activity) => !!activity.activity_id);
    formData.append('has_activities', hasActivities ? '1' : '0');

    const activities = this.forecastActivities.map((item) => ({
      activity_id: item.linked ? item.activity_id || null : null,
      department_id: item.linked ? null : item.department_id || null,
      received_date: item.received_date || null,
      reference_month: this.normalizeMonthInput(item.reference_month),
      forecast_month: item.forecast_month || null,
      forecast_week: item.forecast_week || null,
      forecast_day: item.forecast_day || null,
      forecast_hour: item.forecast_hour || null,
      notes: item.notes || null,
      attachments: item.attachments || []
    }));
    formData.append('activities_json', JSON.stringify(activities));

    this.forecastActivities.forEach((item, index) => {
      (item.newAttachments || []).forEach((file: File) => {
        formData.append(`activity_attachments_${index}`, file);
      });
    });
    return formData;
  }

  handleImportFile(event: Event) {
    const input = event.target as HTMLInputElement;
    const file = input.files?.[0];
    if (!file) return;
    this.importFileName = file.name;
    const extension = file.name.split('.').pop()?.toLowerCase();
    if (extension === 'xlsx') {
      const reader = new FileReader();
      reader.onload = () => {
        const data = new Uint8Array(reader.result as ArrayBuffer);
        const workbook = XLSX.read(data, { type: 'array', cellDates: true });
        const sheetName = workbook.SheetNames[0];
        const sheet = workbook.Sheets[sheetName];
        const rows = XLSX.utils.sheet_to_json(sheet, { header: 1, defval: '' }) as any[][];
        if (!rows.length) {
          this.importHeaders = [];
          this.importPreviewRows = [];
          return;
        }
        const headers = rows[0].map((header) => String(header || '').trim()).filter(Boolean);
        const dateHeaders = new Set(['birth_date', 'hired_at', 'experience_45_days', 'experience_90_days', 'start_date', 'end_date']);
        const bodyRows = rows.slice(1).filter((row) => row.some((cell) => String(cell || '').trim().length));
        this.importHeaders = headers;
        this.importPreviewRows = bodyRows.map((row) => {
          const obj: Record<string, any> = {};
          headers.forEach((header, idx) => {
            const value = row[idx];
            obj[header] = dateHeaders.has(header) ? this.toImportDate(value) : value ?? '';
          });
          return obj;
        });
      };
      reader.readAsArrayBuffer(file);
      return;
    }
    const reader = new FileReader();
    reader.onload = () => {
      const content = String(reader.result || '');
      const parsed = this.parseCsv(content);
      this.importHeaders = parsed.headers;
      this.importPreviewRows = parsed.rows;
    };
    reader.readAsText(file);
  }

  confirmImport() {
    if (!this.tenantId || !this.userId || !this.importPreviewRows.length) return;
    this.importLoading = true;
    this.service.createImport({
      tenant_id: this.tenantId,
      created_by: this.userId,
      import_type: this.importType,
      file_name: this.importFileName || null,
      rows: this.importPreviewRows
    }).subscribe({
      next: (response: any) => {
        this.importLoading = false;
        this.importErrorRows = Array.isArray(response?.errors) ? response.errors : [];
        if (this.importErrorRows.length) {
          this.importFeedback = `Importação concluída com ${this.importErrorRows.length} erro(s).`;
          this.loadImports();
          this.loadEmployees();
          this.loadDashboard();
          return;
        }
        this.importFeedback = 'Importação concluída com sucesso.';
        this.loadImports();
        this.loadEmployees();
        this.loadDashboard();
        this.closeModals();
      },
      error: () => {
        this.importLoading = false;
      }
    });
  }

  downloadLookupSheet(type: 'sectors' | 'departments' | 'roles') {
    if (!this.tenantId) {
      return;
    }
    this.exportLoading = true;
    const request$ =
      type === 'sectors'
        ? this.service.listSectorsForExport(this.tenantId)
        : type === 'departments'
          ? this.service.listDepartmentsForExport(this.tenantId)
          : this.service.listRolesForExport(this.tenantId);
    request$.subscribe({
      next: (rows: any[]) => {
        const { fileName, headers } = this.getLookupTemplate(type);
        const data = [headers, ...rows.map((row) => headers.map((header) => row[header] ?? ''))];
        const worksheet = XLSX.utils.aoa_to_sheet(data);
        const workbook = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(workbook, worksheet, 'Dados');
        XLSX.writeFile(workbook, fileName);
        this.exportLoading = false;
      },
      error: () => {
        this.exportLoading = false;
      }
    });
  }

  downloadFailedImportRows(imp: ControlDpImport) {
    if (!this.tenantId || !imp?.id) {
      return;
    }
    this.exportLoading = true;
    this.service.listImportFailedRows(this.tenantId, imp.id).subscribe({
      next: (rows) => {
        const baseHeaders = ['row_index', '__error'];
        const payloadKeys = new Set<string>();
        (rows || []).forEach((row) => {
          Object.keys(row || {}).forEach((key) => {
            if (!baseHeaders.includes(key)) {
              payloadKeys.add(key);
            }
          });
        });
        const headers = [...baseHeaders, ...Array.from(payloadKeys)];
        const data = [headers, ...(rows || []).map((row) => headers.map((header) => row?.[header] ?? ''))];
        const worksheet = XLSX.utils.aoa_to_sheet(data);
        const workbook = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(workbook, worksheet, 'Erros');
        const fileName = `controlop-import-errors-${imp.id}.xlsx`;
        XLSX.writeFile(workbook, fileName);
        this.exportLoading = false;
      },
      error: () => {
        this.exportLoading = false;
      }
    });
  }

  private parseCsv(content: string) {
    const lines = content.split(/\r?\n/).filter((line) => line.trim().length);
    if (!lines.length) {
      return { headers: [], rows: [] };
    }
    const delimiter = lines[0].includes(';') ? ';' : ',';
    const headers = lines[0].split(delimiter).map((h) => h.trim());
    const rows = lines.slice(1).map((line) => {
      const cols = line.split(delimiter);
      const row: Record<string, any> = {};
      headers.forEach((header, idx) => {
        row[header] = (cols[idx] || '').trim();
      });
      return row;
    });
    return { headers, rows };
  }

  private ensureOccurrencesLoaded(): Promise<ControlDpOccurrence[]> {
    if (!this.tenantId) {
      return Promise.resolve([]);
    }
    if (this.occurrencesLoaded) {
      return Promise.resolve(this.allOccurrences);
    }
    if (this.occurrencesLoading) {
      return new Promise((resolve) => {
        const interval = setInterval(() => {
          if (!this.occurrencesLoading) {
            clearInterval(interval);
            resolve(this.allOccurrences);
          }
        }, 200);
      });
    }
    this.occurrencesLoading = true;
    return new Promise((resolve) => {
      this.service.listOccurrences(this.tenantId!).subscribe({
        next: (rows) => {
          this.allOccurrences = rows || [];
          this.occurrencesLoaded = true;
          this.occurrencesLoading = false;
          resolve(this.allOccurrences);
        },
        error: () => {
          this.allOccurrences = [];
          this.occurrencesLoaded = true;
          this.occurrencesLoading = false;
          resolve(this.allOccurrences);
        }
      });
    });
  }

  private buildBusinessReportPdf(departmentId: number, occurrences: ControlDpOccurrence[]) {
    const departmentLabel = this.getDepartmentLabel(departmentId);
    const employees = this.scopedEmployees.filter(
      (emp) => String(emp.department_id || '') === String(departmentId)
    );
    const totalEmployees = employees.length;
    const statusCounts = { active: 0, on_leave: 0, terminated: 0, in_experience: 0, other: 0 };
    employees.forEach((emp) => {
      const status = String(emp.status || '').toLowerCase();
      if (status === 'active') statusCounts.active += 1;
      else if (status === 'on_leave') statusCounts.on_leave += 1;
      else if (status === 'terminated') statusCounts.terminated += 1;
      else if (status === 'in_experience') statusCounts.in_experience += 1;
      else statusCounts.other += 1;
    });

    const employeeIds = new Set(employees.map((emp) => emp.id));
    const deptOccurrences = occurrences.filter((occ) => employeeIds.has(occ.employee_id));
    const absenceEmployees = new Set(
      deptOccurrences.filter((occ) => String(occ.type || '').toUpperCase() === 'ABSENCE').map((occ) => occ.employee_id)
    );
    const lateEmployees = new Set(
      deptOccurrences.filter((occ) => String(occ.type || '').toUpperCase() === 'LATE').map((occ) => occ.employee_id)
    );
    const absenceRate = totalEmployees ? (absenceEmployees.size / totalEmployees) * 100 : 0;
    const lateRate = totalEmployees ? (lateEmployees.size / totalEmployees) * 100 : 0;
    const turnoverRate = totalEmployees ? (statusCounts.terminated / totalEmployees) * 100 : 0;

    const tenureDays = employees
      .map((emp) => (emp.hired_at ? new Date(emp.hired_at) : null))
      .filter((date) => date && !Number.isNaN(date.getTime())) as Date[];
    const avgTenureLabel = tenureDays.length ? this.formatAverageTenure(tenureDays) : '—';

    let maleCount = 0;
    let femaleCount = 0;
    employees.forEach((emp) => {
      const gender = String(emp.gender || '').trim().toLowerCase();
      if (['m', 'male', 'masculino'].includes(gender)) {
        maleCount += 1;
      } else if (['f', 'female', 'feminino'].includes(gender)) {
        femaleCount += 1;
      }
    });
    const totalGender = maleCount + femaleCount;
    const malePercent = totalGender ? (maleCount / totalGender) * 100 : 0;
    const femalePercent = totalGender ? (femaleCount / totalGender) * 100 : 0;

    const activities = this.businessActivities.filter(
      (activity) => String(activity.department_id || '') === String(departmentId)
    );

    const forecastEntries = this.forecasts.reduce(
      (acc, forecast) => {
        const activitiesForDept = (forecast.activities || []).filter((activity: any) => {
          const activityDept = this.getForecastActivityDepartmentId(activity);
          return String(activityDept || '') === String(departmentId);
        });
        if (!activitiesForDept.length) {
          return acc;
        }
        acc.push({
          title: forecast.title || 'Sem titulo',
          created_at: forecast.created_at || null,
          activities: activitiesForDept
        });
        return acc;
      },
      [] as Array<{ title: string; created_at: string | null; activities: any[] }>
    );

    const doc = new jsPDF({ unit: 'pt', format: 'a4' });
    const margin = 40;
    const pageWidth = doc.internal.pageSize.getWidth();
    const pageHeight = doc.internal.pageSize.getHeight();
    let cursor = 40;

    const nextLine = (lines: string[]) => {
      lines.forEach((line) => {
        if (cursor > pageHeight - 40) {
          doc.addPage();
          cursor = 40;
        }
        doc.text(line, margin, cursor);
        cursor += 16;
      });
    };

    const addText = (text: string) => {
      const lines = doc.splitTextToSize(text, pageWidth - margin * 2);
      nextLine(lines);
    };

    const addSectionTitle = (title: string) => {
      if (cursor > pageHeight - 60) {
        doc.addPage();
        cursor = 40;
      }
      doc.setFont('helvetica', 'bold');
      doc.setFontSize(13);
      doc.text(title, margin, cursor);
      cursor += 18;
      doc.setFont('helvetica', 'normal');
      doc.setFontSize(11);
    };

    doc.setFont('helvetica', 'bold');
    doc.setFontSize(16);
    doc.text('Relatorio Raio X do Negocio', margin, cursor);
    cursor += 22;
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(11);
    addText(`Departamento: ${departmentLabel}`);
    addText(`Data do relatorio: ${this.formatReportDate(new Date())}`);
    const user = this.session.getUser();
    const userName = (user && (user['name'] || user['full_name'] || user['email'])) || 'Usuario';
    addText(`Extraido por: ${userName}`);

    addSectionTitle('Indicadores do time');
    addText(`Quantidade de colaboradores: ${totalEmployees}`);
    addText(`Colaboradores ativos: ${statusCounts.active}`);
    addText(`Colaboradores em afastamento: ${statusCounts.on_leave}`);
    addText(`Colaboradores desligados: ${statusCounts.terminated}`);
    addText(`Colaboradores em experiencia: ${statusCounts.in_experience}`);
    if (statusCounts.other) {
      addText(`Outros status: ${statusCounts.other}`);
    }
    addText(`Taxa de faltas: ${absenceRate.toFixed(1)}%`);
    addText(`Taxa de atrasos: ${lateRate.toFixed(1)}%`);
    addText(`Taxa de turnover: ${turnoverRate.toFixed(1)}%`);
    addText(`Tempo medio de empresa: ${avgTenureLabel}`);
    addText(`% masculino: ${malePercent.toFixed(1)}%`);
    addText(`% feminino: ${femalePercent.toFixed(1)}%`);

    addSectionTitle('Detalhes do negocio');
    addText(`Atividades cadastradas: ${activities.length}`);
    if (!activities.length) {
      addText('Nenhuma atividade cadastrada para este departamento.');
    } else {
      activities.forEach((activity) => {
        const title = activity.title || 'Sem titulo';
        const model = activity.business_model || '—';
        const journey = activity.journey || '—';
        addText(`- ${title} | Modelo: ${model} | Jornada: ${journey}`);
      });
    }

    addSectionTitle('Forecast');
    if (!forecastEntries.length) {
      addText('Nenhum forecast registrado para este departamento.');
    } else {
      forecastEntries.forEach((entry: { title: string; created_at: string | null; activities: any[] }) => {
        const createdAt = entry.created_at ? this.formatReportDate(entry.created_at) : '—';
        addText(`Forecast: ${entry.title} (Criado em ${createdAt})`);
        entry.activities.forEach((activity: any) => {
          const activityTitle = activity.activity_title || 'Geral';
          const received = activity.received_date ? this.formatReportDate(activity.received_date) : '—';
          const reference = activity.reference_month ? this.formatReportDate(activity.reference_month) : '—';
          const monthValue = activity.forecast_month ?? '—';
          addText(`- ${activityTitle} | Recebido: ${received} | Ref: ${reference} | Mes: ${monthValue}`);
        });
      });
    }

    const sanitizedDept = departmentLabel.replace(/[^a-z0-9-_]+/gi, '-').toLowerCase();
    doc.save(`relatorio-raiox-${sanitizedDept || 'departamento'}.pdf`);
    this.setReportFeedback('success', 'Relatorio gerado com sucesso.');
  }

  private buildClosingReportPdf(departmentId: number | null) {
    const departmentLabel = departmentId ? this.getDepartmentLabel(departmentId) : 'Todos';
    let closings = this.closings;
    if (departmentId) {
      closings = closings.filter((closing) => String(closing.department_id || '') === String(departmentId));
    }
    if (this.reportClosingDate) {
      closings = closings.filter(
        (closing) => this.toInputDate(closing.closing_date) === this.reportClosingDate
      );
    }

    const doc = new jsPDF({ unit: 'pt', format: 'a4' });
    const margin = 40;
    const pageWidth = doc.internal.pageSize.getWidth();
    const pageHeight = doc.internal.pageSize.getHeight();
    let cursor = 40;

    const nextLine = (lines: string[]) => {
      lines.forEach((line) => {
        if (cursor > pageHeight - 40) {
          doc.addPage();
          cursor = 40;
        }
        doc.text(line, margin, cursor);
        cursor += 16;
      });
    };

    const addText = (text: string) => {
      const lines = doc.splitTextToSize(text, pageWidth - margin * 2);
      nextLine(lines);
    };

    const addSectionTitle = (title: string) => {
      if (cursor > pageHeight - 60) {
        doc.addPage();
        cursor = 40;
      }
      doc.setFont('helvetica', 'bold');
      doc.setFontSize(13);
      doc.text(title, margin, cursor);
      cursor += 18;
      doc.setFont('helvetica', 'normal');
      doc.setFontSize(11);
    };

    doc.setFont('helvetica', 'bold');
    doc.setFontSize(16);
    doc.text('Relatorio de Fechamento', margin, cursor);
    cursor += 22;
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(11);
    addText(`Departamento: ${departmentLabel}`);
    addText(`Data do relatorio: ${this.formatReportDate(new Date())}`);
    const user = this.session.getUser();
    const userName = (user && (user['name'] || user['full_name'] || user['email'])) || 'Usuario';
    addText(`Extraido por: ${userName}`);
    addText(`Data do fechamento: ${this.reportClosingDate ? this.formatReportDate(this.reportClosingDate) : 'Todos'}`);

    addSectionTitle('Fechamentos');
    if (!closings.length) {
      addText('Nenhum fechamento encontrado para os filtros informados.');
    } else {
      closings.forEach((closing) => {
        const closingDate = closing.closing_date ? this.formatReportDate(closing.closing_date) : '—';
        const dept = this.getDepartmentLabel(closing.department_id);
        addText(`- ${dept} | ${closingDate} | Meta: ${closing.goal ?? '—'} | Realizado: ${closing.achieved ?? '—'} | %: ${closing.delivery_percent ?? '—'}`);
        if (closing.notes) {
          addText(`  Obs: ${closing.notes}`);
        }
      });
    }

    const sanitizedDept = departmentLabel.replace(/[^a-z0-9-_]+/gi, '-').toLowerCase();
    const dateSuffix = this.reportClosingDate || 'todos';
    doc.save(`relatorio-fechamento-${sanitizedDept || 'departamentos'}-${dateSuffix}.pdf`);
    this.setReportFeedback('success', 'Relatorio gerado com sucesso.');
  }

  private formatReportDate(value: any) {
    if (!value) {
      return '—';
    }
    const date = value instanceof Date ? value : new Date(value);
    if (Number.isNaN(date.getTime())) {
      return String(value);
    }
    const day = `${date.getDate()}`.padStart(2, '0');
    const month = `${date.getMonth() + 1}`.padStart(2, '0');
    const year = date.getFullYear();
    return `${day}/${month}/${year}`;
  }

  private formatAverageTenure(dates: Date[]) {
    const now = new Date();
    const totalDays = dates.reduce((sum, date) => sum + this.diffInDays(date, now), 0);
    const avgDays = Math.round(totalDays / dates.length);
    const months = Math.floor(avgDays / 30);
    if (months < 1) {
      return `${avgDays} dias`;
    }
    return `${months} meses`;
  }

  getBarPercent(count: number, max: number) {
    if (!max) {
      return 0;
    }
    return Math.round((count / max) * 100);
  }

  private aggregateDistribution(items: ControlDpEmployee[], field: keyof ControlDpEmployee, fallback: string) {
    const total = items.length || 1;
    const counts = new Map<string, number>();
    items.forEach((item) => {
      const raw = item[field];
      const label = raw ? String(raw) : fallback;
      counts.set(label, (counts.get(label) || 0) + 1);
    });
    return Array.from(counts.entries())
      .map(([label, count]) => ({
        label,
        count,
        percent: Math.round((count / total) * 100)
      }))
      .sort((a, b) => b.count - a.count)
      .slice(0, 8);
  }

  private addDays(date: Date, days: number) {
    const next = new Date(date);
    next.setDate(next.getDate() + days);
    return next;
  }

  private diffInDays(from: Date, to: Date) {
    const ms = to.getTime() - from.getTime();
    return Math.max(0, Math.floor(ms / (1000 * 60 * 60 * 24)));
  }

  private toImportDate(value: any) {
    if (value === null || value === undefined || value === '') {
      return '';
    }
    if (value instanceof Date && !Number.isNaN(value.getTime())) {
      return value.toISOString().slice(0, 10);
    }
    if (typeof value === 'number' && Number.isFinite(value)) {
      const parsed = XLSX.SSF.parse_date_code(value);
      if (parsed) {
        const year = String(parsed.y).padStart(4, '0');
        const month = String(parsed.m).padStart(2, '0');
        const day = String(parsed.d).padStart(2, '0');
        return `${year}-${month}-${day}`;
      }
      return value;
    }
    if (typeof value === 'string') {
      const trimmed = value.trim();
      if (!trimmed) return '';
      const match = trimmed.match(/^(\d{2})\/(\d{2})\/(\d{4})$/);
      if (match) {
        return `${match[3]}-${match[2]}-${match[1]}`;
      }
      return trimmed;
    }
    return value;
  }

  private getTemplateDefinition(type: string) {
    const templates: Record<string, { headers: string[]; sample: string[]; fileName: string }> = {
      EMPLOYEES: {
        headers: [
          'status',
          'full_name',
          'employee_code',
          'cpf',
          'rg',
          'birth_date',
          'birth_location',
          'race',
          'gender',
          'marital_status',
          'mother_name',
          'father_name',
          'education_level',
          'personal_email',
          'corporate_email',
          'phone',
          'cep',
          'street',
          'number',
          'complement',
          'neighborhood',
          'city',
          'state',
          'emergency_contact_name',
          'emergency_contact_phone',
          'hired_at',
          'esocial_code',
          'ctps_number',
          'pis',
          'pcd',
          'cbo_code',
          'bank',
          'bank_branch',
          'bank_account',
          'bank_account_type',
          'contract_type',
          'role_id',
          'sector_id',
          'department_id',
          'location_id',
          'notes'
        ],
        sample: [
          'ACTIVE',
          'Maria Aparecida',
          'DP-2025-001',
          '123.456.789-00',
          '12.345.678-9',
          '1994-06-12',
          'Sao Paulo - SP',
          'Parda',
          'F',
          'Casado(a)',
          'Ana dos Santos',
          'Jose dos Santos',
          'Ensino superior completo',
          'maria@email.com',
          'maria@empresa.com',
          '(11) 99999-0000',
          '01000-000',
          'Rua das Flores',
          '123',
          'Apto 45',
          'Centro',
          'Sao Paulo',
          'SP',
          'Joao Santos',
          '(11) 98888-1111',
          '2024-02-15',
          'ESOC-9981',
          '1234567',
          '123.45678.90-1',
          '0',
          '2521-05',
          'Banco do Brasil',
          '1234',
          '56789-0',
          'Corrente',
          'CLT',
          '1',
          '13',
          '22',
          '5',
          'Registro inicial'
        ],
        fileName: 'controlop-import-employees.xlsx'
      },
      ABSENCES: {
        headers: ['cpf', 'full_name', 'start_date', 'end_date', 'description', 'notes'],
        sample: ['123.456.789-00', 'Maria Aparecida', '2024-03-01', '2024-03-02', 'Falta medica', 'Atestado entregue'],
        fileName: 'controlop-import-absences.xlsx'
      },
      LATES: {
        headers: ['cpf', 'full_name', 'start_date', 'description', 'notes'],
        sample: ['123.456.789-00', 'Maria Aparecida', '2024-03-05', 'Atraso de 30 minutos', 'Transito intenso'],
        fileName: 'controlop-import-lates.xlsx'
      }
    };
    return templates[type] || templates['EMPLOYEES'];
  }

  private defaultEmployeeFeedbackForm(): EmployeeFeedbackFormDraft {
    return {
      feedback_id: null,
      feedback_template_id: '',
      notes: '',
      collaborator_notes: '',
      scheduled_for: ''
    };
  }

  private validateEmployeeFeedbackRequiredAnswers() {
    for (let index = 0; index < this.employeeFeedbackQuestions.length; index += 1) {
      const question = this.employeeFeedbackQuestions[index];
      if (!question.required) {
        continue;
      }
      const normalized = this.normalizeEmployeeFeedbackAnswer(question, this.employeeFeedbackQuestionAnswer(question.id));
      if (!this.employeeFeedbackHasAnswer(normalized)) {
        return `Pergunta obrigatória sem resposta: ${question.title || `Pergunta ${index + 1}`}.`;
      }
    }
    return '';
  }

  private serializeEmployeeFeedbackAnswers() {
    const answers = this.employeeFeedbackQuestions.map((question) => ({
      question_id: question.id,
      question_type: question.question_type,
      answer: this.normalizeEmployeeFeedbackAnswer(question, this.employeeFeedbackQuestionAnswer(question.id))
    }));
    return JSON.stringify({ answers });
  }

  private normalizeEmployeeFeedbackAnswer(question: EmployeeFeedbackQuestion, value: any) {
    if (this.isEmployeeFeedbackMultipleQuestion(question)) {
      const values = Array.isArray(value) ? value.map((item) => String(item).trim()).filter(Boolean) : [];
      return Array.from(new Set(values));
    }
    if (this.isEmployeeFeedbackNumberQuestion(question)) {
      if (value === null || value === undefined || value === '') {
        return null;
      }
      const parsed = Number(value);
      return Number.isFinite(parsed) ? parsed : null;
    }
    if (this.isEmployeeFeedbackDateQuestion(question) || this.isEmployeeFeedbackChoiceQuestion(question) || this.isEmployeeFeedbackEmailQuestion(question)) {
      const normalized = String(value || '').trim();
      return normalized || null;
    }
    const normalizedText = String(value || '').trim();
    return normalizedText || null;
  }

  private employeeFeedbackHasAnswer(value: any) {
    if (Array.isArray(value)) {
      return value.length > 0;
    }
    if (typeof value === 'number') {
      return Number.isFinite(value);
    }
    return !!String(value || '').trim();
  }

  private parseEmployeeFeedbackAnswers(raw?: string | null): Record<string, any> {
    if (!raw) {
      return {};
    }
    try {
      const parsed = typeof raw === 'string' ? JSON.parse(raw) : raw;
      if (Array.isArray(parsed)) {
        return parsed.reduce((acc: Record<string, any>, item: any) => {
          const questionId = Number(item?.question_id || 0);
          if (questionId) {
            acc[String(questionId)] = item?.answer ?? null;
          }
          return acc;
        }, {});
      }
      if (Array.isArray(parsed?.answers)) {
        return parsed.answers.reduce((acc: Record<string, any>, item: any) => {
          const questionId = Number(item?.question_id || 0);
          if (questionId) {
            acc[String(questionId)] = item?.answer ?? null;
          }
          return acc;
        }, {});
      }
      if (parsed && typeof parsed === 'object') {
        const entries = Object.entries(parsed);
        const result: Record<string, any> = {};
        entries.forEach(([key, value]) => {
          if (/^\d+$/.test(String(key))) {
            result[String(key)] = value;
          }
        });
        return result;
      }
      return {};
    } catch (_error) {
      return {};
    }
  }

  private buildEmployeeFeedbackAnswersModel(
    questions: EmployeeFeedbackQuestion[],
    prefilledAnswers?: Record<string, any>
  ): Record<string, any> {
    const source = prefilledAnswers || {};
    const nextAnswers: Record<string, any> = {};
    questions.forEach((question) => {
      const key = String(question.id);
      const value = source[key];
      if (this.isEmployeeFeedbackMultipleQuestion(question)) {
        nextAnswers[key] = Array.isArray(value) ? value.map((item) => String(item)) : [];
        return;
      }
      if (value === undefined || value === null) {
        nextAnswers[key] = '';
        return;
      }
      nextAnswers[key] = value;
    });
    return nextAnswers;
  }

  private toDateTimeLocalInput(value?: string | null) {
    if (!value) {
      return '';
    }
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) {
      return '';
    }
    const year = date.getFullYear();
    const month = `${date.getMonth() + 1}`.padStart(2, '0');
    const day = `${date.getDate()}`.padStart(2, '0');
    const hours = `${date.getHours()}`.padStart(2, '0');
    const minutes = `${date.getMinutes()}`.padStart(2, '0');
    return `${year}-${month}-${day}T${hours}:${minutes}`;
  }

  private getLookupTemplate(type: 'sectors' | 'departments' | 'roles') {
    if (type === 'departments') {
      return {
        headers: ['id', 'name', 'sector_id'],
        fileName: 'controlop-departments.xlsx'
      };
    }
    if (type === 'roles') {
      return {
        headers: ['id', 'name', 'tenant_id'],
        fileName: 'controlop-roles.xlsx'
      };
    }
    return {
      headers: ['id', 'name'],
      fileName: 'controlop-sectors.xlsx'
    };
  }

  private defaultEmployeeForm() {
    return {
      status: 'ACTIVE',
      full_name: '',
      employee_code: '',
      cpf: '',
      rg: '',
      birth_date: '',
      birth_location: '',
      race: '',
      gender: 'NOT_INFORMED',
      marital_status: '',
      mother_name: '',
      father_name: '',
      education_level: '',
      personal_email: '',
      corporate_email: '',
      phone: '',
      cep: '',
      street: '',
      number: '',
      complement: '',
      neighborhood: '',
      city: '',
      state: '',
      emergency_contact_name: '',
      emergency_contact_phone: '',
      hired_at: '',
      experience_45_days: '',
      experience_90_days: '',
      esocial_code: '',
      ctps_number: '',
      pis: '',
      pcd: 0,
      cbo_code: '',
      bank: '',
      bank_branch: '',
      bank_account: '',
      bank_account_type: '',
      contract_type: '',
      salary: '',
      role_id: '',
      sector_id: '',
      department_id: '',
      location_id: '',
      notes: ''
    };
  }

  private defaultExtraForm() {
    return {
      extra_notes: '',
      extra_json: {
        badge_code: '',
        access_profile: '',
        shift: '',
        uniform_size: ''
      }
    };
  }

  private defaultBusinessForm() {
    return {
      title: '',
      description: '',
      business_model: '',
      journey: '',
      sla: '',
      tma: '',
      tmo: '',
      tme: '',
      tmc: '',
      department_id: '',
      client_systems: '',
      acert_systems: '',
      supplier_systems: '',
      observations: ''
    };
  }

  private defaultForecastForm() {
    return {
      title: ''
    };
  }

  private defaultClosingForm() {
    return {
      department_id: '',
      closing_date: '',
      goal: '',
      achieved: '',
      delivery_percent: '',
      notes: ''
    };
  }

  private defaultForecastActivity() {
    return {
      activity_id: '',
      department_id: '',
      linked: false,
      received_date: '',
      reference_month: '',
      forecast_month: '',
      forecast_week: '',
      forecast_day: '',
      forecast_hour: '',
      notes: '',
      attachments: [],
      newAttachments: []
    };
  }

  private normalizeMonthInput(value: any) {
    if (!value) {
      return null;
    }
    const raw = String(value).trim();
    if (!raw) {
      return null;
    }
    if (/^\d{4}-\d{2}$/.test(raw)) {
      return `${raw}-01`;
    }
    return raw;
  }

  private getForecastActivityMissingFields(activity: any) {
    const missing: string[] = [];
    if (activity?.linked) {
      if (!String(activity.activity_id || '').trim()) {
        missing.push('activity');
      }
    } else if (!String(activity?.department_id || '').trim()) {
      missing.push('department');
    }
    if (!String(activity?.received_date || '').trim()) {
      missing.push('received_date');
    }
    if (!String(activity?.reference_month || '').trim()) {
      missing.push('reference_month');
    }
    if (!String(activity?.forecast_month ?? '').toString().trim()) {
      missing.push('forecast_month');
    }
    return missing;
  }

  private setForecastFeedback(kind: string, message: string) {
    this.forecastFeedbackKind = kind;
    this.forecastFeedback = message;
  }

  private clearForecastFeedback() {
    this.forecastFeedbackKind = '';
    this.forecastFeedback = '';
  }

  private setForecastPageFeedback(kind: string, message: string) {
    this.forecastPageFeedbackKind = kind;
    this.forecastPageFeedback = message;
  }

  private clearForecastPageFeedback() {
    this.forecastPageFeedbackKind = '';
    this.forecastPageFeedback = '';
  }

  private setReportFeedback(kind: string, message: string) {
    this.reportFeedbackKind = kind;
    this.reportFeedback = message;
  }

  private clearReportFeedback() {
    this.reportFeedbackKind = '';
    this.reportFeedback = '';
  }

  updateClosingDeliveryPercent() {
    const goal = this.toNumberOrNull(this.closingForm.goal);
    const achieved = this.toNumberOrNull(this.closingForm.achieved);
    if (goal === null || achieved === null || goal === 0) {
      this.closingForm.delivery_percent = '';
      return;
    }
    const percent = (achieved / goal) * 100;
    this.closingForm.delivery_percent = Number.isFinite(percent) ? percent.toFixed(2) : '';
  }

  private toMonthInput(value?: string | null) {
    if (!value) {
      return '';
    }
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) {
      const raw = String(value).trim();
      if (/^\d{4}-\d{2}$/.test(raw)) {
        return raw;
      }
      return '';
    }
    const year = date.getFullYear();
    const month = `${date.getMonth() + 1}`.padStart(2, '0');
    return `${year}-${month}`;
  }

  private appendBusinessFormValue(formData: FormData, key: string, value: any) {
    if (value === undefined || value === null || value === '') {
      return;
    }
    formData.append(key, String(value));
  }

  private buildBusinessFormData() {
    const formData = new FormData();
    if (this.tenantId) {
      formData.append('tenant_id', String(this.tenantId));
    }
    this.appendBusinessFormValue(formData, 'department_id', this.businessForm.department_id);
    this.appendBusinessFormValue(formData, 'title', this.businessForm.title);
    this.appendBusinessFormValue(formData, 'description', this.businessForm.description);
    this.appendBusinessFormValue(formData, 'business_model', this.businessForm.business_model);
    this.appendBusinessFormValue(formData, 'journey', this.businessForm.journey);
    this.appendBusinessFormValue(formData, 'sla', this.businessForm.sla);
    this.appendBusinessFormValue(formData, 'tma', this.businessForm.tma);
    this.appendBusinessFormValue(formData, 'tme', this.businessForm.tme);
    this.appendBusinessFormValue(formData, 'tmo', this.businessForm.tmo);
    this.appendBusinessFormValue(formData, 'tmc', this.businessForm.tmc);
    this.appendBusinessFormValue(formData, 'observations', this.businessForm.observations);
    formData.append('client_systems', JSON.stringify(this.businessClientSystems || []));
    formData.append('acert_systems', JSON.stringify(this.businessAcertSystems || []));
    formData.append('supplier_systems', JSON.stringify(this.businessSupplierSystems || []));

    if (this.businessProcessFile) {
      formData.append('process_file', this.businessProcessFile);
    }
    if (this.businessMeoFile) {
      formData.append('meo_file', this.businessMeoFile);
    }
    this.businessOtherProcessAttachments.forEach((file) => {
      formData.append('other_attachments', file);
    });
    this.businessManualAttachments.forEach((file) => {
      formData.append('manual_attachments', file);
    });
    return formData;
  }

  toggleBusinessSection(section: 'details' | 'times' | 'processes' | 'systems' | 'manuals') {
    if (section === 'details') {
      this.showBusinessDetails = !this.showBusinessDetails;
    } else if (section === 'times') {
      this.showBusinessTimes = !this.showBusinessTimes;
    } else if (section === 'processes') {
      this.showBusinessProcesses = !this.showBusinessProcesses;
    } else if (section === 'systems') {
      this.showBusinessSystems = !this.showBusinessSystems;
    } else {
      this.showBusinessManuals = !this.showBusinessManuals;
    }
  }

  addBusinessKeyword(type: 'client' | 'acert' | 'supplier') {
    const raw =
      type === 'client'
        ? this.businessClientInput
        : type === 'acert'
          ? this.businessAcertInput
          : this.businessSupplierInput;
    const value = raw.trim();
    if (!value) {
      return;
    }
    const target =
      type === 'client'
        ? this.businessClientSystems
        : type === 'acert'
          ? this.businessAcertSystems
          : this.businessSupplierSystems;
    if (!target.includes(value)) {
      target.push(value);
    }
    if (type === 'client') {
      this.businessClientInput = '';
    } else if (type === 'acert') {
      this.businessAcertInput = '';
    } else {
      this.businessSupplierInput = '';
    }
  }

  private pushBusinessHistory(id: number, action: string, before: any, after: any) {
    const user = this.session.getUser() as Record<string, any> | null;
    const userName = (user && (user['name'] || user['full_name'] || user['email'])) || 'Usuario';
    const changes = this.diffBusiness(before, after);
    if (!this.businessHistory[id]) {
      this.businessHistory[id] = [];
    }
    this.businessHistory[id] = [
      {
        action,
        at: new Date().toISOString(),
        user_name: userName,
        before: before ? { ...before } : null,
        after: after ? { ...after } : null,
        changes
      },
      ...this.businessHistory[id]
    ];
  }

  private diffBusiness(before: any, after: any) {
    const fields: Array<{ key: string; label: string }> = [
      { key: 'title', label: 'Titulo' },
      { key: 'business_model', label: 'Modelo do negocio' },
      { key: 'journey', label: 'Jornada' },
      { key: 'department_id', label: 'Departamento' },
      { key: 'description', label: 'Descricao' },
      { key: 'sla', label: 'SLA' },
      { key: 'tma', label: 'TMA' },
      { key: 'tme', label: 'TME' },
      { key: 'tmo', label: 'TMO' },
      { key: 'tmc', label: 'TMC' },
      { key: 'client_systems', label: 'Sistemas cliente' },
      { key: 'acert_systems', label: 'Sistemas internos' },
      { key: 'supplier_systems', label: 'Sistemas fornecedores' },
      { key: 'observations', label: 'Observacao' }
    ];
    if (!before) {
      return [];
    }
    return fields
      .map((field) => {
        const prev = before[field.key];
        const next = after?.[field.key];
        const prevValue = Array.isArray(prev) ? prev.join(', ') : prev ?? '';
        const nextValue = Array.isArray(next) ? next.join(', ') : next ?? '';
        if (String(prevValue) === String(nextValue)) {
          return null;
        }
        return { label: field.label, before: prevValue, after: nextValue };
      })
      .filter((item): item is { label: string; before: any; after: any } => !!item);
  }

  removeBusinessKeyword(type: 'client' | 'acert' | 'supplier', value: string) {
    if (type === 'client') {
      this.businessClientSystems = this.businessClientSystems.filter((item) => item !== value);
    } else if (type === 'acert') {
      this.businessAcertSystems = this.businessAcertSystems.filter((item) => item !== value);
    } else {
      this.businessSupplierSystems = this.businessSupplierSystems.filter((item) => item !== value);
    }
  }

  fillEmployeeTestData() {
    const roleId = this.roles.length ? this.roles[0].id : '';
    const sectorId = this.sectors.length ? this.sectors[0].id : '';
    const departmentsForSector = sectorId
      ? this.departments.filter((dept) => String(dept.sector_id) === String(sectorId))
      : [];
    const departmentId = departmentsForSector.length ? departmentsForSector[0].id : '';
    const locationsForDepartment = departmentId
      ? this.locations.filter((loc) => String(loc.department_id) === String(departmentId))
      : [];
    const locationId = locationsForDepartment.length ? locationsForDepartment[0].id : '';
    this.employeeForm = {
      ...this.employeeForm,
      status: 'ACTIVE',
      full_name: 'Maria Aparecida dos Santos',
      employee_code: 'DP-2025-001',
      cpf: '123.456.789-00',
      rg: '12.345.678-9',
      birth_date: '1994-06-12',
      birth_location: 'Sao Paulo - SP',
      race: 'Parda',
      gender: 'F',
      marital_status: 'Casado(a)',
      mother_name: 'Ana dos Santos',
      father_name: 'Jose dos Santos',
      education_level: 'Ensino superior completo',
      personal_email: 'maria.santos@email.com',
      corporate_email: 'maria.santos@empresa.com',
      phone: '(11) 99999-0000',
      cep: '01000-000',
      street: 'Rua das Flores',
      number: '123',
      complement: 'Apto 45',
      neighborhood: 'Centro',
      city: 'Sao Paulo',
      state: 'SP',
      emergency_contact_name: 'Joao Santos',
      emergency_contact_phone: '(11) 98888-1111',
      hired_at: '2024-02-15',
      esocial_code: 'ESOC-9981',
      ctps_number: '1234567',
      pis: '123.45678.90-1',
      pcd: 0,
      cbo_code: '2521-05',
      bank: 'Banco do Brasil',
      bank_branch: '1234',
      bank_account: '56789-0',
      bank_account_type: 'Corrente',
      contract_type: 'CLT',
      salary: '3200.00',
      role_id: roleId,
      sector_id: sectorId,
      department_id: '',
      location_id: '',
      notes: 'Registro de teste para homologacao.'
    };
    if (roleId) {
      this.onRoleChange();
    }
    if (sectorId) {
      this.onSectorChange();
      this.employeeForm.department_id = departmentId;
      this.onDepartmentChange();
      this.employeeForm.location_id = locationId;
    }
  }

  private toNumberOrNull(value: any) {
    if (value === null || value === undefined || value === '') {
      return null;
    }
    let normalized = value;
    if (typeof normalized === 'string' && normalized.includes(',')) {
      normalized = normalized.replace(/\./g, '').replace(',', '.');
    }
    const parsed = Number(normalized);
    return Number.isNaN(parsed) ? null : parsed;
  }

  private isSalaryEmpty(value: any) {
    return value === null || value === undefined || value === '';
  }

  private toInputDate(value?: string | null) {
    if (!value) {
      return '';
    }
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) {
      return '';
    }
    const year = date.getFullYear();
    const month = `${date.getMonth() + 1}`.padStart(2, '0');
    const day = `${date.getDate()}`.padStart(2, '0');
    return `${year}-${month}-${day}`;
  }

  private defaultOccurrenceForm() {
    return {
      type: 'ABSENCE',
      start_date: '',
      end_date: '',
      description: '',
      notes: '',
      promoted_role_id: '',
      promoted_salary: ''
    };
  }

  onOccurrenceTypeChange() {
    if (this.occurrenceForm.type !== 'PROMOTION') {
      this.occurrenceForm.promoted_role_id = '';
      this.occurrenceForm.promoted_salary = '';
    }
  }

  onOccurrenceFileSelected(event: Event) {
    const input = event.target as HTMLInputElement;
    const file = input.files?.[0];
    if (!file) return;
    this.occurrenceAttachmentFile = file;
    this.occurrenceAttachmentName = file.name;
    this.occurrenceAttachmentDragging = false;
  }

  onOccurrenceFileDrop(event: DragEvent) {
    event.preventDefault();
    this.occurrenceAttachmentDragging = false;
    const file = event.dataTransfer?.files?.[0];
    if (!file) return;
    this.occurrenceAttachmentFile = file;
    this.occurrenceAttachmentName = file.name;
  }

  onOccurrenceDragOver(event: DragEvent) {
    event.preventDefault();
    this.occurrenceAttachmentDragging = true;
  }

  onOccurrenceDragLeave(event: DragEvent) {
    event.preventDefault();
    this.occurrenceAttachmentDragging = false;
  }

  clearOccurrenceAttachment() {
    this.occurrenceAttachmentFile = null;
    this.occurrenceAttachmentName = '';
    this.occurrenceAttachmentDragging = false;
  }

  private finalizeOccurrenceSave() {
    if (!this.selectedEmployee) {
      this.closeModals();
      return;
    }
    this.loadEmployeeExtras(this.selectedEmployee.id);
    this.closeModals();
  }

  private getSavedTab(): TabKey | null {
    try {
      const value = localStorage.getItem(this.tabStorageKey) as TabKey | null;
      const allowedTabs: TabKey[] = [
        'dashboard',
        'collaborators',
        'imports',
        'extras',
        'business',
        'forecast',
        'closing',
        'reports'
      ];
      return allowedTabs.includes(value as TabKey) ? value : null;
    } catch (_error) {
      return null;
    }
  }
}
